import { useState, useEffect, useCallback, useRef, useContext } from 'react';
import * as XLSX from 'xlsx';
import api from '../../hooks/useApi';
import { formatMoney, formatDate } from '../../utils/format';
import { fmtDateTimeLong } from '../../utils/dates.js';
import { sanitiseQuantityInput, commitQuantity } from '../../utils/quantityField.js';
import { mergeProblems, countProblem, defaultKeeper, totalUnits, COUNT_MAX } from '../../utils/inventoryMerge.js';
import Modal from '../shared/Modal';
import QRCode from 'qrcode';
import { useToast } from '../ui/Toast';
import { AppContext, useApp } from '../../context';
import { canAccess } from '../../utils/rbac';
import useRefreshOnFocus from '../../hooks/useRefreshOnFocus';
import SowSelect from '../shared/SowSelect';

const STATUSES = ['incoming', 'in_stock', 'in_transit', 'allocated', 'deployed', 'decommissioned'];
const STATUS_LABELS = { incoming: 'Incoming', in_stock: 'In Stock', in_transit: 'In Transit', allocated: 'Allocated', deployed: 'Deployed', decommissioned: 'Decommissioned' };
const STATUS_COLORS = { incoming: 'bg-purple-100 text-purple-700', in_stock: 'bg-emerald-100 text-emerald-700', in_transit: 'bg-sky-100 text-sky-700', allocated: 'bg-amber-100 text-amber-700', deployed: 'bg-blue-100 text-blue-700', decommissioned: 'bg-gray-100 text-gray-500' };
const VALID_TRANSITIONS = { incoming: ['in_stock','decommissioned'], in_stock: ['allocated','deployed','in_transit','decommissioned'], in_transit: ['in_stock','allocated','deployed','decommissioned'], allocated: ['deployed','in_stock','in_transit','decommissioned'], deployed: ['in_stock','in_transit','decommissioned'], decommissioned: [] };
const TRANSITION_LABELS = { in_stock: '↩ Return to Available', in_transit: '🚚 Mark In Transit', allocated: '📦 Allocate to SOW', deployed: '🚀 Deploy', decommissioned: '🗑 Decommission' };
const TRANSITION_COLORS = { in_stock: 'bg-emerald-500 text-emerald-950 hover:bg-emerald-600', in_transit: 'bg-sky-500 text-white hover:bg-sky-600', allocated: 'bg-amber-500 text-amber-950 hover:bg-amber-600', deployed: 'bg-blue-600 text-white hover:bg-blue-700', decommissioned: 'bg-gray-400 text-gray-950 hover:bg-gray-500' };
const PAGE_SIZE = 50;
const EXPORT_PAGE_SIZE = 200; // the most the inventory API returns in one request

function SortHeader({ col, label, right, sortCol, sortDir, onSort }) {
  const active = sortCol === col;
  return (
    <th onClick={() => onSort(col)}
      className={`px-2 py-1.5 text-[8px] uppercase font-bold border-b bg-gray-50 cursor-pointer select-none whitespace-nowrap ${right ? 'text-right' : 'text-left'} ${active ? 'text-navy' : 'text-gray-500 hover:text-gray-700'}`}>
      {label} {active ? (sortDir === 'asc' ? '▲' : '▼') : <span className="text-gray-300">↕</span>}
    </th>
  );
}

// ─── MAIN PAGE ──────────────────────────────────────────────────────────────

export default function InventoryPage() {
  const { detailId: navDetailId, detailSub: navDetailSub } = useContext(AppContext);
  // Adding, editing and adjusting stock is inventory:manage; scanning and
  // moving it is the page (pen test AD25996, finding AD16).
  const canManage = canAccess('inventory:manage');
  const { toast } = useToast();

  const [tab, setTab] = useState('items');
  const [locations, setLocations] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [sows, setSows] = useState([]);
  const [detailItem, setDetailItem] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createPrefill, setCreatePrefill] = useState(null);
  const [productDeepLinkId, setProductDeepLinkId] = useState(null); // catalog id to auto-open in By Product
  const [itemsVersion, setItemsVersion] = useState(0); // bumped when a split adds records to the list
  const navDetailHandled = useRef(null);

  const loadShared = useCallback(() => {
    Promise.all([
      api.get('inventory/locations'),
      api.get('product-catalog').catch(() => []),
      api.get('sows').catch(() => []),
    ]).then(([l, c, s]) => { setLocations(l); setCatalog(c); setSows(s); });
  }, []);

  useEffect(() => { loadShared(); }, [loadShared]);
  useRefreshOnFocus(loadShared);

  // Auto-open from navigation. A `product` sub-target (from the Product Catalog
  // stock counts) opens the By Product view on that product; otherwise the id is
  // an inventory item (e.g. global search) and we open its detail modal.
  useEffect(() => {
    if (!navDetailId || navDetailId === navDetailHandled.current) return;
    navDetailHandled.current = navDetailId;
    if (navDetailSub === 'product') {
      setTab('products');
      setProductDeepLinkId(navDetailId);
      return;
    }
    api.get(`inventory/${navDetailId}`).then(item => { if (item?.id) { setDetailItem(item); setTab('items'); } }).catch(() => {});
  }, [navDetailId, navDetailSub]);

  const handleCreate = async (data) => {
    await api.post('inventory', data);
    setShowCreate(false);
    setCreatePrefill(null);
    loadShared();
    toast('Stock added', 'success');
  };

  const handleUpdateItem = async (id, patch) => {
    await api.put(`inventory/${id}`, patch);
    if (detailItem?.id === id) {
      api.get(`inventory/${id}`).then(fresh => { if (fresh?.id) setDetailItem(fresh); }).catch(() => {});
    }
  };

  const catalogMap = Object.fromEntries(catalog.map(c => [c.id, c]));

  const TABS = [
    { key: 'items',      label: 'All Items' },
    { key: 'products',   label: 'By Product' },
    { key: 'byname',     label: 'By Name' },
    { key: 'locations',  label: 'Locations' },
    { key: 'bulkstock',  label: 'Bulk Stock' },
    { key: 'stocktake',  label: 'Stock Take' },
  ];

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-extrabold text-navy">Inventory</h2>
          <div className="text-[9px] text-gray-500">Track stock levels, locations and allocations</div>
        </div>
        <div className="flex items-center gap-2">
          {/* Scan a run of labels with the phone camera and apply one action to all of them */}
          <a href="/?bulkscan=1"
            className="px-3 py-1.5 border border-navy text-navy text-[10px] font-semibold rounded hover:bg-gray-50">
            Bulk scan
          </a>
          {canManage && (
            <button onClick={() => { setCreatePrefill(null); setShowCreate(true); }}
              className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80">
              + Add Stock
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-end border-b border-gray-200 mb-4 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 text-[10px] font-medium border-b-2 -mb-px whitespace-nowrap flex-shrink-0 ${tab === t.key ? 'text-gold border-gold font-semibold' : 'text-gray-400 border-transparent hover:text-gray-600'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'products' && (
        <ProductsView
          catalog={catalog}
          locations={locations}
          onAddStock={canManage ? (prefill) => { setCreatePrefill(prefill); setShowCreate(true); } : null}
          onOpenItem={setDetailItem}
          initialProductId={productDeepLinkId}
          onConsumedInitial={() => setProductDeepLinkId(null)}
        />
      )}
      {tab === 'items' && (
        <ItemsView
          locations={locations}
          catalogMap={catalogMap}
          sows={sows}
          onOpenItem={setDetailItem}
          onRefresh={loadShared}
          canManage={canManage}
          reloadKey={itemsVersion}
        />
      )}
      {tab === 'byname' && (
        <ByNameView catalog={catalog} onRefresh={loadShared} canManage={canManage} />
      )}
      {tab === 'locations' && (
        <LocationsView locations={locations} onRefresh={loadShared} canManage={canManage} />
      )}
      {tab === 'bulkstock' && (
        <BulkStockView catalog={catalog} locations={locations} canManage={canManage} />
      )}
      {tab === 'stocktake' && (
        <StockTakeView locations={locations} sows={sows} canManage={canManage} />
      )}

      {detailItem && (
        <ItemDetailModal
          item={detailItem}
          locations={locations}
          sows={sows}
          canManage={canManage}
          onClose={() => setDetailItem(null)}
          onUpdate={handleUpdateItem}
          onRefreshItem={(id) => api.get(`inventory/${id}`).then(fresh => { if (fresh?.id) setDetailItem(fresh); }).catch(() => {})}
          onRecordsChanged={() => setItemsVersion(v => v + 1)}
        />
      )}
      {showCreate && (
        <AddInventoryModal
          catalog={catalog}
          locations={locations}
          prefill={createPrefill}
          onClose={() => { setShowCreate(false); setCreatePrefill(null); }}
          onSave={handleCreate}
        />
      )}
    </div>
  );
}

// ─── PRODUCTS VIEW ──────────────────────────────────────────────────────────

function ProductsView({ catalog, locations, onAddStock, onOpenItem, initialProductId, onConsumedInitial }) {
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState('description');
  const [sortDir, setSortDir] = useState('asc');
  const [selectedProduct, setSelectedProduct] = useState(null);

  const loadSummary = () => {
    setLoading(true);
    api.get('inventory/stock-by-product').then(r => { setSummary(Array.isArray(r) ? r : []); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { loadSummary(); }, []);

  // Deep-linked from the Product Catalog stock counts: open that product's stock
  // breakdown straight away once the summary has loaded.
  useEffect(() => {
    if (!initialProductId || loading) return;
    const match = summary.find(p => p.catalog_id === initialProductId);
    if (match) { setSelectedProduct(match); setSearch(match.description || match.manufacturer_part_number || ''); }
    onConsumedInitial && onConsumedInitial();
  }, [initialProductId, loading, summary]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const filtered = summary
    .filter(p => !search || p.description?.toLowerCase().includes(search.toLowerCase()) || p.manufacturer_part_number?.toLowerCase().includes(search.toLowerCase()) || p.manufacturer?.toLowerCase().includes(search.toLowerCase()) || p.category?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const av = a[sortCol] ?? '', bv = b[sortCol] ?? '';
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const sortProps = { sortCol, sortDir, onSort: toggleSort };

  // Summary totals
  const totals = summary.reduce((acc, p) => ({ in_stock: acc.in_stock + (p.in_stock||0), incoming: acc.incoming + (p.incoming||0), allocated: acc.allocated + (p.allocated||0), total: acc.total + (p.total||0) }), { in_stock: 0, incoming: 0, allocated: 0, total: 0 });

  return (
    <div>
      {/* Summary stat row */}
      <div className="grid grid-cols-4 gap-2 mb-3">
        {[
          { label: 'Products tracked', val: summary.length, color: 'text-navy' },
          { label: 'In Stock', val: totals.in_stock, color: 'text-emerald-600' },
          { label: 'Incoming', val: totals.incoming, color: 'text-purple-600' },
          { label: 'Allocated / Deployed', val: totals.allocated, color: 'text-amber-600' },
        ].map(s => (
          <div key={s.label} className="bg-white border border-gray-200 rounded p-2">
            <div className="text-[8px] text-gray-500 uppercase font-bold">{s.label}</div>
            <div className={`text-lg font-extrabold ${s.color}`}>{s.val}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 mb-2">
        <input className="form-input flex-1 text-[10px]" placeholder="Search product name, part no., manufacturer, category…"
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div className="bg-white border border-gray-200 rounded overflow-x-auto">
        <table className="w-full">
          <thead><tr>
            <SortHeader col="manufacturer_part_number" label="MPN" {...sortProps} />
            <SortHeader col="description" label="Product" {...sortProps} />
            <SortHeader col="category" label="Category" {...sortProps} />
            <SortHeader col="manufacturer" label="Manufacturer" {...sortProps} />
            <SortHeader col="in_stock" label="In Stock" right {...sortProps} />
            <SortHeader col="incoming" label="Incoming" right {...sortProps} />
            <SortHeader col="allocated" label="Allocated" right {...sortProps} />
            <SortHeader col="deployed" label="Deployed" right {...sortProps} />
            <SortHeader col="total" label="Total" right {...sortProps} />
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan="9" className="text-center py-8 text-[10px] text-gray-400">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan="9" className="text-center py-6 text-[10px] text-gray-400">No products with stock found</td></tr>}
            {filtered.map(p => (
              <tr key={p.catalog_id}
                onClick={() => setSelectedProduct(p)}
                className="border-b border-gray-100 hover:bg-gold/5 cursor-pointer">
                <td className="px-2 py-1.5 text-[9px] font-mono font-bold text-navy whitespace-nowrap">{p.manufacturer_part_number || '—'}</td>
                <td className="px-2 py-1.5">
                  <div className="text-[10px] font-semibold">{p.description}</div>
                </td>
                <td className="px-2 py-1.5 text-[9px] text-gray-500">{p.category || '—'}</td>
                <td className="px-2 py-1.5 text-[9px] text-gray-500">{p.manufacturer || '—'}</td>
                <td className="px-2 py-1.5 text-right">
                  {p.in_stock > 0 ? <span className="text-[10px] font-bold text-emerald-600">{p.in_stock}</span> : <span className="text-[9px] text-gray-300">—</span>}
                </td>
                <td className="px-2 py-1.5 text-right">
                  {p.incoming > 0 ? <span className="text-[10px] font-bold text-purple-600">{p.incoming}</span> : <span className="text-[9px] text-gray-300">—</span>}
                </td>
                <td className="px-2 py-1.5 text-right">
                  {p.allocated > 0 ? <span className="text-[10px] font-bold text-amber-600">{p.allocated}</span> : <span className="text-[9px] text-gray-300">—</span>}
                </td>
                <td className="px-2 py-1.5 text-right">
                  {p.deployed > 0 ? <span className="text-[10px] font-bold text-blue-600">{p.deployed}</span> : <span className="text-[9px] text-gray-300">—</span>}
                </td>
                <td className="px-2 py-1.5 text-right">
                  <span className="text-[10px] font-extrabold text-navy">{p.total}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedProduct && (
        <ProductStockModal
          product={selectedProduct}
          locations={locations}
          onAddStock={onAddStock && (() => { onAddStock({ product_catalog_id: selectedProduct.catalog_id, name: selectedProduct.description, manufacturer: selectedProduct.manufacturer, category: selectedProduct.category, unit_cost: selectedProduct.unit_cost }); })}
          onOpenItem={onOpenItem}
          onClose={() => { setSelectedProduct(null); loadSummary(); }}
        />
      )}
    </div>
  );
}

// ─── PRODUCT STOCK MODAL ─────────────────────────────────────────────────────

function ProductStockModal({ product, locations, onAddStock, onOpenItem, onClose }) {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showItems, setShowItems] = useState(false);
  const [showMove, setShowMove] = useState(false);
  const [moveFromLocId, setMoveFromLocId] = useState('');
  const [moveToLocId, setMoveToLocId] = useState('');
  const [moveQty, setMoveQty] = useState(1);
  const [moving, setMoving] = useState(false);

  const loadItems = () => {
    setLoading(true);
    api.get('inventory', { product_catalog_id: product.catalog_id, limit: 500 })
      .then(r => { setItems(r.items ?? (Array.isArray(r) ? r : [])); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { loadItems(); }, [product.catalog_id]);

  // Group items by location
  const byLocation = {};
  for (const item of items) {
    const locId = item.location_id || '__none__';
    const locName = item.location_name || '(No location)';
    if (!byLocation[locId]) byLocation[locId] = { locId, locName, in_stock: 0, incoming: 0, allocated: 0, deployed: 0, in_transit: 0, total: 0 };
    byLocation[locId][item.status] = (byLocation[locId][item.status] || 0) + 1;
    byLocation[locId].total++;
  }
  const locationRows = Object.values(byLocation).sort((a, b) => b.in_stock - a.in_stock);

  // Available in-stock items for each source location
  const inStockByLoc = {};
  for (const item of items) {
    if (item.status === 'in_stock') {
      const locId = item.location_id || '__none__';
      if (!inStockByLoc[locId]) inStockByLoc[locId] = [];
      inStockByLoc[locId].push(item);
    }
  }
  const moveableItems = inStockByLoc[moveFromLocId] || [];
  const maxQty = moveableItems.length;

  const handleBulkMove = async () => {
    if (!moveToLocId || moveFromLocId === moveToLocId) { toast('Select different source and destination', 'error'); return; }
    if (maxQty === 0) { toast('No In Stock items at source location', 'error'); return; }
    const toMove = moveableItems.slice(0, Math.min(moveQty, maxQty));
    setMoving(true);
    try {
      await api.post('inventory/bulk-move', { item_ids: toMove.map(i => i.id), location_id: moveToLocId === '__none__' ? null : moveToLocId });
      toast(`Moved ${toMove.length} item(s)`, 'success');
      loadItems();
      setShowMove(false);
    } catch (e) {
      toast(e.message || 'Move failed', 'error');
    } finally {
      setMoving(false);
    }
  };

  const total = product.total || items.length;
  const inStock = product.in_stock || items.filter(i => i.status === 'in_stock').length;
  const incoming = product.incoming || items.filter(i => i.status === 'incoming').length;
  const allocated = product.allocated || items.filter(i => i.status === 'allocated').length;
  const deployed = product.deployed || items.filter(i => i.status === 'deployed').length;

  return (
    <Modal title={`${product.manufacturer_part_number ? product.manufacturer_part_number + ' — ' : ''}Stock Summary`} onClose={onClose} width="max-w-3xl"
      footer={
        <div className="flex items-center gap-2">
          {onAddStock && <button onClick={onAddStock} className="px-3 py-1.5 text-[10px] font-semibold bg-navy text-white rounded hover:bg-navy/80">+ Add Stock</button>}
          <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Close</button>
        </div>
      }>

      {/* Product header */}
      <div className="mb-3">
        <div className="text-[13px] font-extrabold font-mono text-navy">{product.manufacturer_part_number || product.description}</div>
        {product.manufacturer_part_number && <div className="text-[10px] text-gray-500">{product.description}</div>}
        <div className="flex gap-2 mt-1 flex-wrap">
          {product.manufacturer && <span className="text-[8px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{product.manufacturer}</span>}
          {product.category && <span className="text-[8px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">{product.category}</span>}
          {product.unit_cost > 0 && <span className="text-[8px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">{formatMoney(product.unit_cost)} per unit</span>}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-5 gap-1.5 mb-4">
        {[
          { label: 'Total', val: total, bg: 'bg-navy/5', text: 'text-navy' },
          { label: 'In Stock', val: inStock, bg: 'bg-emerald-50', text: 'text-emerald-700' },
          { label: 'Incoming', val: incoming, bg: 'bg-purple-50', text: 'text-purple-700' },
          { label: 'Allocated', val: allocated, bg: 'bg-amber-50', text: 'text-amber-700' },
          { label: 'Deployed', val: deployed, bg: 'bg-blue-50', text: 'text-blue-700' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded p-2 text-center`}>
            <div className="text-[7px] text-gray-500 uppercase font-bold">{s.label}</div>
            <div className={`text-xl font-extrabold ${s.text}`}>{s.val}</div>
          </div>
        ))}
      </div>

      {/* By Location table */}
      <div className="mb-4">
        <h4 className="text-[9px] font-bold uppercase text-gray-500 mb-1.5">Stock by Location</h4>
        {loading ? <div className="text-center py-4 text-[10px] text-gray-400">Loading…</div> : (
          <div className="bg-white border border-gray-200 rounded overflow-x-auto">
            <table className="w-full">
              <thead><tr>
                {['Location','In Stock','Incoming','Allocated','Deployed','Total'].map(h => (
                  <th key={h} className={`px-2 py-1 text-[8px] uppercase font-bold text-gray-500 border-b bg-gray-50 ${h === 'Location' ? 'text-left' : 'text-right'}`}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {locationRows.length === 0 && <tr><td colSpan="6" className="text-center py-4 text-[10px] text-gray-400">No items</td></tr>}
                {locationRows.map(row => (
                  <tr key={row.locId} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-2 py-1 text-[10px] font-semibold">{row.locName}</td>
                    <td className="px-2 py-1 text-right"><span className={row.in_stock > 0 ? 'text-[10px] font-bold text-emerald-600' : 'text-[9px] text-gray-300'}>{row.in_stock || '—'}</span></td>
                    <td className="px-2 py-1 text-right"><span className={row.incoming > 0 ? 'text-[10px] font-bold text-purple-600' : 'text-[9px] text-gray-300'}>{row.incoming || '—'}</span></td>
                    <td className="px-2 py-1 text-right"><span className={row.allocated > 0 ? 'text-[10px] font-bold text-amber-600' : 'text-[9px] text-gray-300'}>{row.allocated || '—'}</span></td>
                    <td className="px-2 py-1 text-right"><span className={row.deployed > 0 ? 'text-[10px] font-bold text-blue-600' : 'text-[9px] text-gray-300'}>{row.deployed || '—'}</span></td>
                    <td className="px-2 py-1 text-right text-[10px] font-bold text-navy">{row.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Move Stock */}
      <div className="mb-4 border border-gray-200 rounded p-3">
        <button onClick={() => setShowMove(v => !v)} className="text-[9px] font-bold text-indigo-700 hover:underline flex items-center gap-1">
          {showMove ? '▲' : '▼'} Move Stock Between Locations
        </button>
        {showMove && (
          <div className="mt-2 grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">From Location</label>
              <select className="form-select text-[10px]" value={moveFromLocId} onChange={e => setMoveFromLocId(e.target.value)}>
                <option value="">Select source…</option>
                {Object.values(byLocation).filter(l => l.in_stock > 0).map(l => (
                  <option key={l.locId} value={l.locId}>{l.locName} ({l.in_stock} in stock)</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">To Location</label>
              <select className="form-select text-[10px]" value={moveToLocId} onChange={e => setMoveToLocId(e.target.value)}>
                <option value="">Select destination…</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Quantity {moveFromLocId && maxQty > 0 && <span className="text-gray-400">(max {maxQty})</span>}</label>
              <input type="number" className="form-input" min="1" max={maxQty || 1} value={moveQty} onChange={e => setMoveQty(Math.max(1, parseInt(e.target.value) || 1))} />
            </div>
            <div className="flex items-end">
              <button onClick={handleBulkMove} disabled={moving || !moveFromLocId || !moveToLocId || maxQty === 0}
                className="w-full px-3 py-1.5 text-[10px] font-semibold bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-40">
                {moving ? 'Moving…' : `Move ${Math.min(moveQty, maxQty || 0)} items`}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Individual items */}
      <div>
        <button onClick={() => setShowItems(v => !v)}
          className="text-[9px] font-bold text-gray-500 hover:text-gray-700 flex items-center gap-1">
          {showItems ? '▲ Hide' : '▼ Show'} Individual Items ({items.length})
        </button>
        {showItems && (
          <div className="mt-2 bg-white border border-gray-200 rounded overflow-x-auto max-h-64 overflow-y-auto">
            <table className="w-full">
              <thead><tr>
                {['Asset Tag','S/N','Status','Location'].map(h => (
                  <th key={h} className="px-2 py-1 text-[8px] uppercase font-bold text-gray-500 border-b bg-gray-50 text-left sticky top-0">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {items.map(i => (
                  <tr key={i.id} className="border-b border-gray-100 hover:bg-gold/5 cursor-pointer" onClick={() => onOpenItem(i)}>
                    <td className="px-2 py-1 text-[9px] font-mono font-bold text-navy">{i.asset_tag}</td>
                    <td className="px-2 py-1 text-[9px] text-gray-500 font-mono">{i.serial_number || '—'}</td>
                    <td className="px-2 py-1">
                      <span className={`text-[8px] font-semibold px-1.5 py-0.5 rounded ${STATUS_COLORS[i.status]}`}>{STATUS_LABELS[i.status] ?? i.status}</span>
                    </td>
                    <td className="px-2 py-1 text-[9px]">{i.location_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── BY NAME VIEW ────────────────────────────────────────────────────────────

function ByNameView({ catalog, onRefresh, canManage }) {
  const { toast } = useToast();
  const [groups, setGroups]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [linking, setLinking]     = useState(null); // name being linked
  const [catalogSearch, setCatalogSearch] = useState('');
  const [saving, setSaving]       = useState(false);
  const [showCreateFor, setShowCreateFor] = useState(null); // name to create catalog item for
  const [createForm, setCreateForm] = useState({});
  const [creatingCatalog, setCreatingCatalog] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('inventory/group-by-name')
      .then(r => { setGroups(Array.isArray(r) ? r : []); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = groups.filter(g =>
    !search || g.name?.toLowerCase().includes(search.toLowerCase()) || g.category?.toLowerCase().includes(search.toLowerCase())
  );

  const catalogFiltered = catalog.filter(c =>
    !catalogSearch || c.description?.toLowerCase().includes(catalogSearch.toLowerCase()) ||
    c.manufacturer?.toLowerCase().includes(catalogSearch.toLowerCase()) ||
    c.manufacturer_part_number?.toLowerCase().includes(catalogSearch.toLowerCase())
  ).slice(0, 8);

  const assign = async (name, catalogId) => {
    setSaving(true);
    try {
      const r = await api.post('inventory/bulk-assign-catalog', { name, product_catalog_id: catalogId });
      toast(`Linked ${r.updated} item${r.updated !== 1 ? 's' : ''} to catalog`, 'success');
      setLinking(null); setCatalogSearch('');
      load(); onRefresh();
    } catch (e) { toast(e?.data?.error || 'Failed to link', 'error'); }
    setSaving(false);
  };

  const createAndAssign = async (name) => {
    if (!createForm.description?.trim()) return;
    setCreatingCatalog(true);
    try {
      const cat = await api.post('product-catalog', {
        description: createForm.description.trim(),
        manufacturer: createForm.manufacturer || null,
        manufacturer_part_number: createForm.mpn || null,
        category: createForm.category || groups.find(g => g.name === name)?.category || null,
      });
      await assign(name, cat.id);
      setShowCreateFor(null); setCreateForm({});
    } catch (e) { toast(e?.data?.error || 'Failed to create catalog item', 'error'); }
    setCreatingCatalog(false);
  };

  const STAT_COLORS = { in_stock: 'text-emerald-600', incoming: 'text-purple-600', allocated: 'text-amber-600', deployed: 'text-blue-600', decommissioned: 'text-gray-400' };

  return (
    <div>
      <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded text-[9px] text-blue-700">
        Showing <strong>{groups.length}</strong> distinct item name{groups.length !== 1 ? 's' : ''} with no catalog link — group items together by linking them to an existing product catalog entry or creating a new one.
      </div>
      <div className="flex gap-2 mb-3">
        <input className="form-input flex-1 text-[10px]" placeholder="🔍 Filter by name or category…"
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      {loading ? (
        <div className="text-center py-8 text-[10px] text-gray-400">Loading…</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-2 text-left text-[8px] uppercase font-bold text-gray-500">Name</th>
                <th className="px-2 py-2 text-left text-[8px] uppercase font-bold text-gray-500">Category</th>
                <th className="px-2 py-2 text-center text-[8px] uppercase font-bold text-gray-500">Total</th>
                <th className="px-2 py-2 text-left text-[8px] uppercase font-bold text-gray-500">Breakdown</th>
                <th className="px-2 py-2 text-left text-[8px] uppercase font-bold text-gray-500">Catalog action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(g => (
                <>
                  <tr key={g.name} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 text-[10px] font-semibold text-gray-800 max-w-[240px]">
                      <div className="truncate" title={g.name}>{g.name}</div>
                    </td>
                    <td className="px-2 py-2 text-[9px] text-gray-500">{g.category || '—'}</td>
                    <td className="px-2 py-2 text-center text-[11px] font-bold text-navy">{g.total}</td>
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-2 flex-wrap text-[8px] font-semibold">
                        {[['in_stock','Stock'], ['incoming','Incoming'], ['allocated','Alloc'], ['deployed','Deployed'], ['decommissioned','Decomm']].map(([k, lbl]) =>
                          g[k] > 0 ? <span key={k} className={STAT_COLORS[k]}>{g[k]} {lbl}</span> : null
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      {canManage ? (
                        <div className="flex gap-1">
                          <button onClick={() => { setLinking(g.name); setCatalogSearch(''); setShowCreateFor(null); }}
                            className="px-2 py-0.5 text-[8px] font-semibold bg-navy text-white rounded hover:bg-navy/80 whitespace-nowrap">
                            Link to catalog
                          </button>
                          <button onClick={() => { setShowCreateFor(g.name); setLinking(null); setCreateForm({ description: g.name, category: g.category || '' }); }}
                            className="px-2 py-0.5 text-[8px] font-semibold border border-navy text-navy rounded hover:bg-navy/5 whitespace-nowrap">
                            + New catalog item
                          </button>
                        </div>
                      ) : <span className="text-[8px] text-gray-400">Admin required</span>}
                    </td>
                  </tr>

                  {/* Inline link picker */}
                  {linking === g.name && (
                    <tr key={`link-${g.name}`} className="bg-blue-50 border-b border-blue-100">
                      <td colSpan={5} className="px-4 py-3">
                        <div className="text-[9px] font-semibold text-blue-800 mb-2">Select a catalog item to link <span className="font-bold">"{g.name}"</span> ({g.total} items):</div>
                        <input autoFocus className="form-input w-full max-w-sm text-[9px] mb-2"
                          placeholder="Search catalog…" value={catalogSearch}
                          onChange={e => setCatalogSearch(e.target.value)} />
                        <div className="flex flex-col gap-1 max-w-lg">
                          {catalogFiltered.length === 0 && <div className="text-[8px] text-gray-400">No matches — try creating a new catalog item instead.</div>}
                          {catalogFiltered.map(c => (
                            <button key={c.id} disabled={saving}
                              onClick={() => assign(g.name, c.id)}
                              className="text-left px-2 py-1.5 rounded border border-blue-200 bg-white hover:bg-blue-100 text-[9px] flex items-center gap-2">
                              <span className="font-semibold text-navy truncate">{c.description}</span>
                              {c.manufacturer && <span className="text-gray-400">{c.manufacturer}</span>}
                              {c.manufacturer_part_number && <span className="text-gray-400 font-mono">{c.manufacturer_part_number}</span>}
                            </button>
                          ))}
                        </div>
                        <button onClick={() => { setLinking(null); setCatalogSearch(''); }}
                          className="mt-2 text-[8px] text-gray-400 hover:text-gray-600">Cancel</button>
                      </td>
                    </tr>
                  )}

                  {/* Inline create catalog form */}
                  {showCreateFor === g.name && (
                    <tr key={`create-${g.name}`} className="bg-emerald-50 border-b border-emerald-100">
                      <td colSpan={5} className="px-4 py-3">
                        <div className="text-[9px] font-semibold text-emerald-800 mb-2">Create new catalog item for <span className="font-bold">"{g.name}"</span>:</div>
                        <div className="grid grid-cols-2 gap-2 max-w-lg">
                          <div className="col-span-2">
                            <label className="block text-[7px] font-bold text-gray-500 uppercase mb-0.5">Description *</label>
                            <input className="form-input text-[9px]" value={createForm.description || ''}
                              onChange={e => setCreateForm(p => ({ ...p, description: e.target.value }))} />
                          </div>
                          <div>
                            <label className="block text-[7px] font-bold text-gray-500 uppercase mb-0.5">Manufacturer</label>
                            <input className="form-input text-[9px]" value={createForm.manufacturer || ''}
                              onChange={e => setCreateForm(p => ({ ...p, manufacturer: e.target.value }))} />
                          </div>
                          <div>
                            <label className="block text-[7px] font-bold text-gray-500 uppercase mb-0.5">Part Number</label>
                            <input className="form-input text-[9px]" value={createForm.mpn || ''}
                              onChange={e => setCreateForm(p => ({ ...p, mpn: e.target.value }))} />
                          </div>
                          <div>
                            <label className="block text-[7px] font-bold text-gray-500 uppercase mb-0.5">Category</label>
                            <input className="form-input text-[9px]" value={createForm.category || ''}
                              onChange={e => setCreateForm(p => ({ ...p, category: e.target.value }))} />
                          </div>
                        </div>
                        <div className="flex gap-2 mt-2">
                          <button onClick={() => createAndAssign(g.name)} disabled={!createForm.description?.trim() || creatingCatalog}
                            className="px-3 py-1 text-[9px] font-semibold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
                            {creatingCatalog ? 'Creating…' : 'Create & Link all items'}
                          </button>
                          <button onClick={() => { setShowCreateFor(null); setCreateForm({}); }}
                            className="text-[8px] text-gray-400 hover:text-gray-600">Cancel</button>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
              {filtered.length === 0 && !loading && (
                <tr><td colSpan={5} className="text-center py-8 text-[10px] text-gray-400">
                  {groups.length === 0 ? '🎉 All inventory items are linked to a catalog entry' : 'No matching groups'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── ITEMS VIEW ──────────────────────────────────────────────────────────────

function ItemsView({ locations, catalogMap, sows, onOpenItem, onRefresh, canManage, reloadKey }) {
  const { toast } = useToast();
  const { navigate } = useApp();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [sortCol, setSortCol] = useState('updated_at');
  const [sortDir, setSortDir] = useState('desc');
  const [selected, setSelected] = useState(new Set());
  const [showBulkMove, setShowBulkMove] = useState(false);
  const [bulkStatus, setBulkStatus] = useState(null); // target status for bulk transition
  const [showMerge, setShowMerge] = useState(false);
  const [totalValue, setTotalValue] = useState(0);
  const [statusTotals, setStatusTotals] = useState({});
  const [statusRecords, setStatusRecords] = useState({});
  const [exporting, setExporting] = useState(false);

  const load = useCallback((page = 0) => {
    const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE, sort: sortCol, sort_dir: sortDir };
    if (filter) params.status = filter;
    if (search) params.search = search;
    if (categoryFilter) params.category = categoryFilter;
    if (locationFilter) params.location_id = locationFilter;
    api.get('inventory', params).then(r => {
      setItems(r.items ?? (Array.isArray(r) ? r : []));
      setTotal(r.total ?? 0);
      setCurrentPage(page);
      setSelected(new Set());
      if (r.total_value !== undefined) setTotalValue(r.total_value);
      if (r.status_totals) setStatusTotals(r.status_totals);
      if (r.status_records) setStatusRecords(r.status_records);
    });
  }, [filter, search, categoryFilter, locationFilter, sortCol, sortDir]);

  useEffect(() => { load(0); }, [filter, search, categoryFilter, locationFilter, sortCol, sortDir]);
  // A split or a count change in item detail alters rows; reload the page in view.
  useEffect(() => { if (reloadKey) load(currentPage); }, [reloadKey]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  // Costs come back only to holders of trade-cost:see; anyone else sees a dash, not £0.
  const seesCost = canAccess('trade-cost:see');
  const unitCost = (i) => i.catalog_unit_cost != null ? i.catalog_unit_cost : (i.purchase_price || 0);
  const itemValue = (i) => unitCost(i) * Math.max(1, i.quantity || 1);
  const effectiveCategory = (i) => i.catalog_category || (i.category && i.category !== 'other' ? i.category : null);
  const uniqueCategories = [...new Set(items.map(i => effectiveCategory(i)).filter(Boolean))].sort();
  const statusCounts = {};
  STATUSES.forEach(s => statusCounts[s] = statusTotals[s] ?? 0);
  // The cards count units and the list counts records: a bulk line of 25 plugs
  // is 25 on a card and one row in the list and the export.
  const fmtCount = (n) => Number(n || 0).toLocaleString('en-GB');
  // With no status picked the export keeps decommissioned records the list
  // hides, so the button counts them in to match what downloads.
  const exportCount = filter ? total : total + (statusRecords.decommissioned ?? 0);

  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = items.length > 0 && items.every(i => selected.has(i.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map(i => i.id)));
  const selectedItems = items.filter(i => selected.has(i.id));

  const downloadExcel = async () => {
    const exportParams = { limit: EXPORT_PAGE_SIZE, sort: sortCol, sort_dir: sortDir };
    if (filter) exportParams.status = filter;
    else exportParams.include_decommissioned = 1; // full record in the export even though the list hides them
    if (search) exportParams.search = search;
    if (categoryFilter) exportParams.category = categoryFilter;
    if (locationFilter) exportParams.location_id = locationFilter;
    // The API returns at most 200 rows a request, so page through to the end
    // rather than asking for everything at once and quietly getting 200.
    setExporting(true);
    const allItems = [];
    try {
      for (let offset = 0; ; offset += EXPORT_PAGE_SIZE) {
        const resp = await api.get('inventory', { ...exportParams, offset });
        const page = resp.items ?? [];
        allItems.push(...page);
        if (page.length < EXPORT_PAGE_SIZE || allItems.length >= (resp.total ?? 0)) break;
      }
    } catch (e) {
      toast(`Export failed: ${e.message}`, 'error');
      return;
    } finally {
      setExporting(false);
    }
    const wb = XLSX.utils.book_new();
    const headers = ['Asset Tag','Name','Quantity','Serial Number','Category','Status','Location','SOW','Manufacturer','Purchase Price','Expected Delivery','Received Date','Source PO'];
    const rows = allItems.map(i => [i.asset_tag, i.name, i.quantity ?? 1, i.serial_number||'', i.catalog_category || i.category, STATUS_LABELS[i.status]||i.status, i.location_name||'', i.allocated_sow_reference||'', i.manufacturer||'', i.purchase_price!=null?i.purchase_price:'', i.expected_delivery||'', i.received_date||'', i.source_po_number||'']);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = [12,24,8,16,12,12,16,10,16,12,14,14,14].map(w=>({wch:w}));
    XLSX.utils.book_append_sheet(wb, ws, 'Inventory');
    XLSX.writeFile(wb, `inventory-${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const sortProps = { sortCol, sortDir, onSort: toggleSort };
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-2 mb-2">
        <div className="bg-white border border-gray-200 rounded p-2">
          <div className="text-[8px] text-gray-500 uppercase font-bold">Total Value</div>
          <div className="text-base font-extrabold text-navy">{seesCost ? formatMoney(totalValue) : '—'}</div>
        </div>
        {['incoming','in_stock','allocated'].map(s => (
          <button key={s} onClick={() => setFilter(filter === s ? '' : s)}
            className={`bg-white border rounded p-2 text-left transition-all ${filter === s ? 'border-navy ring-1 ring-navy/20' : 'border-gray-200 hover:border-gray-300'}`}>
            <div className="text-[8px] text-gray-500 uppercase font-bold">{STATUS_LABELS[s]}</div>
            <div className={`text-base font-extrabold ${s==='in_stock'?'text-emerald-500':s==='allocated'?'text-amber-500':'text-purple-500'}`}>{fmtCount(statusCounts[s])}</div>
            <div className="text-[8px] text-gray-400">units · {fmtCount(statusRecords[s])} {statusRecords[s] === 1 ? 'line' : 'lines'}</div>
          </button>
        ))}
      </div>
      <div className="flex gap-2 mb-3 items-center">
        {['deployed','decommissioned'].map(s => (
          <button key={s} onClick={() => setFilter(filter === s ? '' : s)}
            className={`px-2 py-1 rounded text-[8px] font-semibold border transition-all ${filter===s?'border-navy bg-navy/5 text-navy':'border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-600'}`}>
            {STATUS_LABELS[s]}: {fmtCount(statusCounts[s])}
          </button>
        ))}
        <span className="text-[8px] text-gray-400 ml-1">— tap to filter{filter !== 'decommissioned' ? ' · decommissioned items are hidden until selected' : ''}</span>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded flex-wrap">
          <span className="text-[10px] font-bold text-indigo-700">{selected.size} item{selected.size>1?'s':''} selected</span>
          <button onClick={() => printLabelsBatch(selectedItems, (msg) => toast(msg, 'error'))} className="px-2 py-1 text-[9px] font-semibold bg-navy text-white rounded hover:bg-navy/80">🖨 Print Labels</button>
          <button onClick={() => setShowBulkMove(true)} className="px-2 py-1 text-[9px] font-semibold bg-indigo-600 text-white rounded hover:bg-indigo-700">Move to Location…</button>
          {/* Bulk status is managing stock; one item's status is on its detail, and a run of labels is Bulk scan. */}
          {canManage && <>
            <span className="w-px h-4 bg-indigo-200" />
            <button onClick={() => setBulkStatus('in_transit')} className="px-2 py-1 text-[9px] font-semibold bg-sky-500 text-white rounded hover:bg-sky-600">🚚 In Transit</button>
            <button onClick={() => setBulkStatus('allocated')} className="px-2 py-1 text-[9px] font-semibold bg-amber-500 text-amber-950 rounded hover:bg-amber-600">📦 Allocate to SOW…</button>
            <button onClick={() => setBulkStatus('deployed')} className="px-2 py-1 text-[9px] font-semibold bg-blue-600 text-white rounded hover:bg-blue-700">🚀 Deploy…</button>
            <button onClick={() => setBulkStatus('decommissioned')} className="px-2 py-1 text-[9px] font-semibold bg-gray-500 text-white rounded hover:bg-gray-600">🗑 Decommission…</button>
            {selected.size > 1 && (
              <button onClick={() => setShowMerge(true)} data-testid="merge-items" className="px-2 py-1 text-[9px] font-semibold bg-white border border-indigo-300 text-indigo-700 rounded hover:bg-indigo-100">⇲ Merge into one…</button>
            )}
          </>}
          <button onClick={() => setSelected(new Set())} className="ml-auto text-[9px] text-gray-500 hover:text-gray-700">✕ Clear selection</button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-2 items-center">
        <input className="form-input flex-1 min-w-[180px] text-[10px]" placeholder="🔍 Search name, asset tag, serial, SOW, location…"
          value={search} onChange={e => setSearch(e.target.value)} />
        <select className="form-select text-[10px] w-36" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
          <option value="">All categories</option>
          {uniqueCategories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="form-select text-[10px] w-40" value={locationFilter} onChange={e => setLocationFilter(e.target.value)}>
          <option value="">All locations</option>
          {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        {filter && (
          <button onClick={() => setFilter('')} className={`px-2 py-1 rounded text-[8px] font-semibold flex items-center gap-1 ${STATUS_COLORS[filter]}`}>
            {STATUS_LABELS[filter]} ✕
          </button>
        )}
        <button onClick={downloadExcel} disabled={exporting}
          title={filter ? undefined : 'Includes decommissioned records, which the list hides'}
          className="px-2 py-1 text-[9px] font-semibold bg-emerald-600 text-white rounded hover:bg-emerald-700 whitespace-nowrap ml-auto disabled:opacity-60">
          {exporting ? 'Exporting…' : `↓ Excel (${fmtCount(exportCount)} rows)`}
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded overflow-x-auto">
        <table className="w-full">
          <thead><tr>
            <th className="px-2 py-1.5 border-b bg-gray-50 w-8">
              <input type="checkbox" checked={allSelected} onChange={toggleAll} className="rounded" />
            </th>
            <SortHeader col="asset_tag" label="Asset Tag" {...sortProps} />
            <SortHeader col="name" label="Name / Serial" {...sortProps} />
            <th className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Catalogue</th>
            <SortHeader col="location_name" label="Location" {...sortProps} />
            <SortHeader col="status" label="Status" {...sortProps} />
            <SortHeader col="purchase_price" label="Value" right {...sortProps} />
          </tr></thead>
          <tbody>
            {items.map(i => {
              const catItem = i.product_catalog_id ? catalogMap[i.product_catalog_id] : null;
              return (
                <tr key={i.id}
                  className={`border-b border-gray-100 hover:bg-gold/5 transition-colors ${i.status==='allocated'?'bg-amber-50/30':i.status==='deployed'?'bg-blue-50/20':i.status==='incoming'?'bg-purple-50/20':i.status==='decommissioned'?'opacity-50':''}`}>
                  <td className="px-2 py-1.5 text-center" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(i.id)} onChange={() => toggleSelect(i.id)} className="rounded" />
                  </td>
                  <td className="px-2 py-1.5 cursor-pointer" onClick={() => onOpenItem(i)}>
                    <span className="text-[10px] font-mono font-semibold text-navy">{i.asset_tag || '—'}</span>
                  </td>
                  <td className="px-2 py-1.5 cursor-pointer" onClick={() => onOpenItem(i)}>
                    <div className="text-[10px] font-semibold">
                      {i.name}
                      {(i.quantity || 1) > 1 && (
                        <span className="ml-1.5 text-[8px] font-bold text-navy bg-gold/20 px-1 py-0.5 rounded whitespace-nowrap">{i.quantity} units</span>
                      )}
                    </div>
                    {i.serial_number && <div className="text-[8px] text-gray-400 font-mono">S/N {i.serial_number}</div>}
                    {(i.catalog_manufacturer||i.manufacturer) && <div className="text-[8px] text-gray-400">{i.catalog_manufacturer||i.manufacturer}</div>}
                  </td>
                  <td className="px-2 py-1.5 cursor-pointer" onClick={() => onOpenItem(i)}>
                    {catItem ? (
                      <div>
                        <div className="text-[8px] font-mono font-bold text-navy">{catItem.manufacturer_part_number || catItem.code}</div>
                        <div className="text-[8px] text-gray-500 truncate max-w-[140px]">{catItem.category || i.category}</div>
                      </div>
                    ) : effectiveCategory(i) ? (
                      <span className="text-[8px] bg-gray-100 text-gray-500 px-1 py-0.5 rounded">{effectiveCategory(i)}</span>
                    ) : <span className="text-[8px] text-gray-300">—</span>}
                  </td>
                  <td className="px-2 py-1.5 cursor-pointer" onClick={() => onOpenItem(i)}>
                    <div className="text-[9px] text-gray-600">{i.location_name || <span className="text-gray-300">—</span>}</div>
                    {/* The paperwork behind the item, reachable from here: the SOW it is
                        allocated to, and for incoming stock the PO that is bringing it in
                        along with the SOW that PO was raised against. */}
                    {(i.status==='allocated'||i.status==='deployed') && (i.allocated_sow_reference||i.notes) && (
                      <div className={`text-[8px] font-semibold mt-0.5 ${i.status==='allocated'?'text-amber-600':'text-blue-600'}`}>
                        {i.status==='allocated'?'📦':'🚀'}{' '}
                        {i.allocated_to_sow_id && i.allocated_sow_reference ? (
                          <button onClick={e => { e.stopPropagation(); navigate('sow-detail', i.allocated_to_sow_id); }}
                            className="hover:underline" title="View SOW">{i.allocated_sow_reference}</button>
                        ) : (i.allocated_sow_reference||i.notes)}
                      </div>
                    )}
                    {i.status==='incoming' && i.source_po_number && (
                      <div className="text-[8px] text-purple-600 mt-0.5">
                        📋{' '}
                        {i.source_po_id ? (
                          <button onClick={e => { e.stopPropagation(); navigate('pos', i.source_po_id); }}
                            className="hover:underline" title="View purchase order">PO {i.source_po_number}</button>
                        ) : `PO ${i.source_po_number}`}
                        {i.po_expected_delivery ? ` · ETA ${formatDate(i.po_expected_delivery)}` : ''}
                      </div>
                    )}
                    {i.status==='incoming' && (i.allocated_to_sow_id || i.po_sow_id) && (i.allocated_sow_reference || i.po_sow_reference) && (
                      <div className="text-[8px] text-purple-600 mt-0.5">
                        📦{' '}
                        <button onClick={e => { e.stopPropagation(); navigate('sow-detail', i.allocated_to_sow_id || i.po_sow_id); }}
                          className="hover:underline" title="View SOW">{i.allocated_sow_reference || i.po_sow_reference}</button>
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 cursor-pointer" onClick={() => onOpenItem(i)}>
                    <span className={`text-[8px] font-semibold px-1.5 py-0.5 rounded ${STATUS_COLORS[i.status]}`}>{STATUS_LABELS[i.status]??i.status}</span>
                  </td>
                  <td className="px-2 py-1.5 text-right cursor-pointer" onClick={() => onOpenItem(i)}>
                    <div className="text-[10px] font-semibold">{seesCost ? formatMoney(itemValue(i)) : '—'}</div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan="7" className="text-center py-6 text-[10px] text-gray-400">No inventory items{filter?` with status "${STATUS_LABELS[filter]}"`:''}</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-2 text-[9px] text-gray-500">
          <span>{currentPage * PAGE_SIZE + 1}–{Math.min((currentPage + 1) * PAGE_SIZE, total)} of {total}</span>
          <div className="flex gap-1">
            <button disabled={currentPage === 0} onClick={() => load(currentPage - 1)} className="px-2 py-0.5 border rounded disabled:opacity-40 hover:bg-gray-50">‹</button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              const page = totalPages <= 7 ? i : (currentPage < 4 ? i : (currentPage > totalPages - 4 ? totalPages - 7 + i : currentPage - 3 + i));
              return <button key={page} onClick={() => load(page)} className={`px-2 py-0.5 border rounded hover:bg-gray-50 ${currentPage === page ? 'border-navy font-bold text-navy bg-navy/5' : ''}`}>{page + 1}</button>;
            })}
            <button disabled={currentPage >= totalPages - 1} onClick={() => load(currentPage + 1)} className="px-2 py-0.5 border rounded disabled:opacity-40 hover:bg-gray-50">›</button>
          </div>
        </div>
      )}

      {showBulkMove && (
        <BulkMoveModal items={selectedItems} locations={locations} onClose={() => setShowBulkMove(false)}
          onMoved={() => { setShowBulkMove(false); setSelected(new Set()); load(currentPage); }} />
      )}

      {showMerge && (
        <MergeModal items={selectedItems} onClose={() => setShowMerge(false)}
          onMerged={() => { setSelected(new Set()); load(currentPage); onRefresh(); }} />
      )}

      {bulkStatus && (
        <BulkStatusModal items={selectedItems} status={bulkStatus} sows={sows} onClose={() => setBulkStatus(null)}
          onDone={() => { setBulkStatus(null); setSelected(new Set()); load(currentPage); onRefresh(); }} />
      )}
    </div>
  );
}

// ─── BULK STATUS MODAL ───────────────────────────────────────────────────────

function BulkStatusModal({ items, status, sows, onClose, onDone }) {
  const { toast } = useToast();
  const [sowId, setSowId] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const usesSow = status === 'allocated' || status === 'deployed';
  const label = STATUS_LABELS[status] || status;
  const actionWord = status === 'allocated' ? 'Allocate' : status === 'deployed' ? 'Deploy' : status === 'decommissioned' ? 'Decommission' : status === 'in_transit' ? 'Mark In Transit' : 'Update';
  // allocate/deploy need a SOW link OR a free-text reason
  const blocked = usesSow && !sowId && !notes.trim();

  const handle = async () => {
    setSaving(true);
    try {
      const r = await api.post('inventory/bulk-status', {
        item_ids: items.map(i => i.id), status,
        allocated_to_sow_id: usesSow ? (sowId || null) : null,
        notes: notes.trim() || null,
      });
      toast(`${actionWord}: ${r.updated} item${r.updated === 1 ? '' : 's'} updated${r.errors?.length ? `, ${r.errors.length} skipped` : ''}`, r.errors?.length ? 'error' : 'success');
      onDone();
    } catch (e) {
      toast(e?.data?.error || e.message || 'Update failed', 'error');
      setSaving(false);
    }
  };

  return (
    <Modal title={`${actionWord} ${items.length} item${items.length > 1 ? 's' : ''}`} onClose={onClose} footer={
      <><button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={handle} disabled={saving || blocked} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">{saving ? 'Saving…' : `${actionWord} ${items.length}`}</button></>
    }>
      <p className="text-[10px] text-gray-500 mb-3">
        Setting <strong>{items.length}</strong> selected item{items.length > 1 ? 's' : ''} to <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${STATUS_COLORS[status]}`}>{label}</span>.
      </p>
      <div className="space-y-2">
        {usesSow && (
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Link to SOW {status === 'allocated' ? '*' : ''}</label>
            <SowSelect sows={sows} value={sowId} onChange={setSowId} placeholder="Search for a SOW… (or leave blank and give a reason)" />
          </div>
        )}
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">
            {usesSow ? 'Reason / notes' : 'Notes'} {usesSow && !sowId ? '*' : '(optional)'}
          </label>
          <input className="form-input" value={notes} onChange={e => setNotes(e.target.value)}
            placeholder={usesSow ? 'e.g. lab/test rig, internal use' : status === 'decommissioned' ? 'e.g. end of life, faulty' : 'optional note'} />
        </div>
      </div>
      {blocked && <p className="text-[8px] text-amber-600 mt-2">Link a SOW or enter a reason to continue.</p>}
      <div className="mt-3 max-h-32 overflow-y-auto">
        <div className="text-[8px] text-gray-400 mb-1">Items affected:</div>
        {items.map(i => <div key={i.id} className="text-[9px] font-mono text-gray-600">{i.asset_tag} — {i.name} <span className="text-gray-400">({STATUS_LABELS[i.status] || i.status})</span></div>)}
      </div>
    </Modal>
  );
}

// ─── MERGE MODAL ─────────────────────────────────────────────────────────────
// Several records of the same stock become one: the record kept holds every
// unit and keeps its tag, and the others are deleted. What would stop the
// merge is known from the rows on screen, so the dialog says so up front
// rather than offering a Merge button that the server then refuses.

function MergeModal({ items: picked, onClose, onMerged }) {
  const { toast } = useToast();
  // The list reloads behind the dialog once the merge lands, which empties the
  // selection; the dialog goes on describing the records it was opened with.
  const [items] = useState(picked);
  const [keepId, setKeepId] = useState(() => defaultKeeper(items)?.id || '');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);
  const problems = mergeProblems(items);
  const total = totalUnits(items);
  const keeper = items.find(i => i.id === keepId);
  const others = items.filter(i => i.id !== keepId);
  const pos = [...new Set(items.map(i => i.source_po_number).filter(Boolean))];

  const merge = async () => {
    setSaving(true);
    try {
      const r = await api.post('inventory/merge', { item_ids: items.map(i => i.id), keep_id: keepId });
      setDone(r);
      onMerged();
    } catch (e) {
      toast(e?.data?.error || e.message || 'Merge failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <Modal title={`Merged into ${done.asset_tag}`} onClose={onClose} footer={
        <><button onClick={() => printLabel({ ...keeper, quantity: done.quantity }, (msg) => toast(msg, 'error'))} className="px-3 py-1 text-[10px] font-semibold border border-gray-300 rounded bg-white hover:bg-gray-50">🖨 Print Label</button>
          <button onClick={onClose} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded">Close</button></>
      }>
        <div className="bg-emerald-50 border border-emerald-200 rounded p-2 space-y-1">
          <div className="text-[10px] font-bold text-emerald-700">{done.asset_tag} now holds {done.quantity.toLocaleString('en-GB')} units</div>
          <div className="text-[9px] text-gray-600">
            {done.merged.join(', ')} {done.merged.length === 1 ? 'has' : 'have'} gone from the list. Their labels still scan to {done.asset_tag}, and its history shows what came in from each.
          </div>
        </div>
      </Modal>
    );
  }

  if (problems.length) {
    return (
      <Modal title={`These ${items.length} records cannot be merged`} onClose={onClose} footer={
        <button onClick={onClose} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded">Back to the list</button>
      }>
        <p className="text-[10px] text-gray-500 mb-2">Records merge when nothing tells their units apart. These differ:</p>
        <ul className="bg-amber-50 border border-amber-200 rounded p-2 space-y-1 list-disc list-inside">
          {problems.map(p => <li key={p} className="text-[9px] text-amber-800">{p}</li>)}
        </ul>
        <div className="mt-3 max-h-40 overflow-y-auto">
          {items.map(i => (
            <div key={i.id} className="text-[9px] text-gray-600">
              <span className="font-mono">{i.asset_tag}</span> · {i.name} · {STATUS_LABELS[i.status] || i.status} · {i.location_name || 'No location'}{i.serial_number ? ` · S/N ${i.serial_number}` : ''}
            </div>
          ))}
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={`Merge ${items.length} records into one`} onClose={onClose} footer={
      <><button onClick={onClose} disabled={saving} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Keep them separate</button>
        <button onClick={merge} disabled={saving || !keeper} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">
          {saving ? 'Merging…' : `Merge into ${keeper?.asset_tag || '…'}`}
        </button></>
    }>
      <p className="text-[10px] text-gray-500 mb-2">
        <strong>{total.toLocaleString('en-GB')} units</strong> of {items[0].name} will sit on one record. Pick the tag to keep:
      </p>
      <div className="border border-gray-200 rounded max-h-52 overflow-y-auto">
        {items.map(i => (
          <label key={i.id} className={`flex items-center gap-2 px-2 py-1.5 border-b border-gray-100 last:border-0 cursor-pointer ${keepId === i.id ? 'bg-gold/10' : ''}`}>
            <input type="radio" name="merge-keep" checked={keepId === i.id} onChange={() => setKeepId(i.id)} />
            <span className="text-[10px] font-mono font-semibold text-navy">{i.asset_tag}</span>
            <span className="text-[9px] text-gray-600">{Math.max(1, i.quantity || 1)} unit{(i.quantity || 1) > 1 ? 's' : ''}</span>
            {i.source_po_number && <span className="text-[8px] text-gray-400">PO {i.source_po_number}</span>}
          </label>
        ))}
      </div>
      <div className="text-[9px] text-gray-600 mt-2 space-y-1">
        <div>{keeper?.asset_tag} keeps its label, notes and history. {others.map(o => o.asset_tag).join(', ')} {others.length === 1 ? 'is' : 'are'} deleted; {others.length === 1 ? 'its label still scans' : 'their labels still scan'} to {keeper?.asset_tag}, and {others.length === 1 ? 'its' : 'their'} history moves across.</div>
        {pos.length > 1 && <div className="text-amber-700">These came in on different POs ({pos.join(', ')}). The merged record shows {keeper?.source_po_number ? `PO ${keeper.source_po_number}` : 'no PO'}.</div>}
      </div>
    </Modal>
  );
}

// ─── BULK MOVE MODAL ─────────────────────────────────────────────────────────

function BulkMoveModal({ items, locations, onClose, onMoved }) {
  const { toast } = useToast();
  const [targetLocId, setTargetLocId] = useState('');
  const [notes, setNotes] = useState('');
  const [moving, setMoving] = useState(false);

  const handle = async () => {
    setMoving(true);
    try {
      await api.post('inventory/bulk-move', { item_ids: items.map(i => i.id), location_id: targetLocId || null, notes: notes || null });
      toast(`Moved ${items.length} item(s)`, 'success');
      onMoved();
    } catch (e) {
      toast(e.message || 'Move failed', 'error');
      setMoving(false);
    }
  };

  return (
    <Modal title="Move Selected Items" onClose={onClose} footer={
      <><button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={handle} disabled={moving || !targetLocId} className="px-3 py-1 text-[10px] font-semibold bg-indigo-600 text-white rounded disabled:opacity-40">{moving ? 'Moving…' : `Move ${items.length} item${items.length>1?'s':''}`}</button></>
    }>
      <p className="text-[10px] text-gray-500 mb-3">Moving <strong>{items.length}</strong> selected item{items.length>1?'s':''} to a new location.</p>
      <div className="space-y-2">
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Destination Location *</label>
          <select className="form-select" value={targetLocId} onChange={e => setTargetLocId(e.target.value)}>
            <option value="">Select location…</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Notes (optional)</label>
          <input className="form-input" value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Stock review Q2" />
        </div>
      </div>
      <div className="mt-3 max-h-32 overflow-y-auto">
        <div className="text-[8px] text-gray-400 mb-1">Items being moved:</div>
        {items.map(i => <div key={i.id} className="text-[9px] font-mono text-gray-600">{i.asset_tag} — {i.name}</div>)}
      </div>
    </Modal>
  );
}

// ─── BULK STOCK ──────────────────────────────────────────────────────────────
// Running quantity per location for products flagged "bulk_tracked" in the catalog.
function BulkStockView({ catalog, locations, canManage }) {
  const { toast } = useToast();
  const [levels, setLevels] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [setEditId, setSetEditId] = useState(null);
  const [setVal, setSetVal] = useState('');
  const [busyKey, setBusyKey] = useState(null);

  const bulkProducts = (catalog || []).filter(c => c.bulk_tracked);

  const load = useCallback(() => { api.get('inventory/stock-levels').then(setLevels).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const adjust = async (level, payload) => {
    const key = level.id || `${payload.product_catalog_id}|${payload.location_id}`;
    setBusyKey(key);
    try {
      await api.post('inventory/stock-levels/adjust', payload);
      await load();
    } catch (e) { toast(e?.data?.error || e.message || 'Adjust failed', 'error'); }
    setBusyKey(null);
    setSetEditId(null);
  };

  const totalQty = levels.reduce((s, l) => s + (l.quantity || 0), 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] text-gray-500">
          {levels.length} bulk line{levels.length === 1 ? '' : 's'} · {totalQty} units. Counts are adjusted on the fly — no individual asset tags.
        </div>
        {canManage && (
          <button onClick={() => setShowAdd(true)} disabled={bulkProducts.length === 0}
            className="px-3 py-1.5 text-[10px] font-semibold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-40"
            title={bulkProducts.length === 0 ? 'Flag a product as "Bulk / quantity tracked" in the Product Catalog first' : ''}>
            + Add Bulk Stock
          </button>
        )}
      </div>

      {bulkProducts.length === 0 && (
        <div className="text-[9px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
          No products are flagged for bulk tracking yet. In <strong>Admin → Product Catalog</strong>, edit a product and tick
          “Bulk / quantity tracked”.
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded overflow-x-auto">
        <table className="w-full">
          <thead><tr>
            {['Product','Location','Quantity', canManage ? 'Adjust' : ''].filter(Boolean).map(h => (
              <th key={h} className={`px-2 py-1.5 text-[8px] uppercase font-bold text-gray-500 border-b bg-gray-50 ${h === 'Adjust' ? 'text-right' : 'text-left'}`}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {levels.length === 0 && <tr><td colSpan="4" className="text-center py-8 text-[10px] text-gray-400">No bulk stock recorded yet</td></tr>}
            {levels.map(l => {
              const key = l.id;
              const busy = busyKey === key;
              return (
                <tr key={key} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-2 py-1.5">
                    <div className="text-[10px] font-semibold">{l.product_description}</div>
                    <div className="text-[8px] font-mono text-gray-400">{l.product_code}</div>
                  </td>
                  <td className="px-2 py-1.5 text-[9px] text-gray-600">{l.location_name || <span className="text-gray-300">No location</span>}</td>
                  <td className="px-2 py-1.5">
                    {setEditId === key ? (
                      <input autoFocus type="number" className="form-input w-20 text-[10px] py-0.5" value={setVal}
                        onChange={e => setSetVal(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') adjust(l, { product_catalog_id: l.product_catalog_id, location_id: l.location_id, set: parseInt(setVal, 10) || 0 }); if (e.key === 'Escape') setSetEditId(null); }} />
                    ) : (
                      <span className="text-base font-extrabold text-navy">{l.quantity}</span>
                    )}
                  </td>
                  {canManage && (
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      {setEditId === key ? (
                        <>
                          <button onClick={() => adjust(l, { product_catalog_id: l.product_catalog_id, location_id: l.location_id, set: parseInt(setVal, 10) || 0 })} disabled={busy} className="text-[9px] font-semibold text-emerald-600 hover:underline mr-2">Save</button>
                          <button onClick={() => setSetEditId(null)} className="text-[9px] text-gray-400 hover:underline">Cancel</button>
                        </>
                      ) : (
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => adjust(l, { product_catalog_id: l.product_catalog_id, location_id: l.location_id, delta: -1 })} disabled={busy || l.quantity <= 0} className="w-6 h-6 rounded border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-30 font-bold">−</button>
                          <button onClick={() => adjust(l, { product_catalog_id: l.product_catalog_id, location_id: l.location_id, delta: 1 })} disabled={busy} className="w-6 h-6 rounded border border-gray-200 text-gray-600 hover:bg-gray-100 font-bold">+</button>
                          <button onClick={() => { setSetEditId(key); setSetVal(String(l.quantity)); }} className="ml-1 text-[9px] text-gold hover:underline">Set…</button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <AddBulkStockModal products={bulkProducts} locations={locations}
          onClose={() => setShowAdd(false)}
          onSave={async (payload) => { await adjust({}, payload); setShowAdd(false); }} />
      )}
    </div>
  );
}

function AddBulkStockModal({ products, locations, onClose, onSave }) {
  const { toast } = useToast();
  const [productId, setProductId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [qty, setQty] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!productId) { toast('Pick a product', 'error'); return; }
    setSaving(true);
    try { await onSave({ product_catalog_id: productId, location_id: locationId || null, set: parseInt(qty, 10) || 0, reason: 'Initial bulk count' }); }
    catch (e) { toast(e?.data?.error || e.message, 'error'); setSaving(false); }
  };

  return (
    <Modal title="Add Bulk Stock" onClose={onClose} footer={
      <><button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={save} disabled={saving || !productId} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">{saving ? 'Saving…' : 'Add'}</button></>
    }>
      <div className="space-y-2">
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Product *</label>
          <select className="form-select" value={productId} onChange={e => setProductId(e.target.value)}>
            <option value="">Select a bulk-tracked product…</option>
            {products.map(p => <option key={p.id} value={p.id}>{p.description} ({p.code})</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Location</label>
          <select className="form-select" value={locationId} onChange={e => setLocationId(e.target.value)}>
            <option value="">No location</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Starting quantity</label>
          <input type="number" className="form-input" value={qty} onChange={e => setQty(e.target.value)} placeholder="e.g. 123" />
        </div>
      </div>
    </Modal>
  );
}

// ─── STOCK TAKE ──────────────────────────────────────────────────────────────

function StockTakeView({ locations, sows, canManage }) {
  const { toast } = useToast();
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [showStart, setShowStart] = useState(false);
  const [startLoc, setStartLoc] = useState('');
  const [starting, setStarting] = useState(false);

  const load = useCallback(() => { api.get('stock-takes').then(setSessions).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const start = async () => {
    if (!startLoc) return;
    setStarting(true);
    try {
      const r = await api.post('stock-takes', { location_id: startLoc });
      setShowStart(false); setStartLoc('');
      load();
      setActiveId(r.id);
    } catch (e) { toast(e?.data?.error || e.message || 'Could not start', 'error'); }
    setStarting(false);
  };

  if (activeId) {
    return <StockTakeSession id={activeId} sows={sows} canManage={canManage} onBack={() => { setActiveId(null); load(); }} />;
  }

  const inProgress = sessions.filter(s => s.status === 'in_progress');
  const closed = sessions.filter(s => s.status !== 'in_progress');

  const Row = ({ s }) => {
    const pct = s.total ? Math.round((s.confirmed / s.total) * 100) : 0;
    return (
      <tr className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => setActiveId(s.id)}>
        <td className="px-2 py-1.5 text-[10px] font-semibold">{s.location_name || '—'}</td>
        <td className="px-2 py-1.5">
          <span className={`text-[8px] font-semibold px-1.5 py-0.5 rounded ${s.status === 'in_progress' ? 'bg-sky-100 text-sky-700' : s.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
            {s.status === 'in_progress' ? 'In Progress' : s.status === 'completed' ? 'Completed' : 'Cancelled'}
          </span>
        </td>
        <td className="px-2 py-1.5 text-[9px] text-gray-600">{s.confirmed}/{s.total} ({pct}%)</td>
        <td className="px-2 py-1.5 text-[9px] text-gray-500">{s.started_by_name || '—'}</td>
        <td className="px-2 py-1.5 text-[9px] text-gray-500">{fmtDateTimeLong ? fmtDateTimeLong(s.started_at) : s.started_at}</td>
        <td className="px-2 py-1.5 text-right text-[9px] text-gold">Open →</td>
      </tr>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] text-gray-500">Count the physical stock at a location, then act on anything missing.</div>
        {canManage && <button onClick={() => setShowStart(true)} className="px-3 py-1.5 text-[10px] font-semibold bg-navy text-white rounded hover:bg-navy/80">+ Start Stock Take</button>}
      </div>

      {inProgress.length > 0 && (
        <div className="mb-4">
          <div className="text-[9px] font-bold text-gray-500 uppercase mb-1">In progress</div>
          <div className="bg-white border border-gray-200 rounded overflow-x-auto">
            <table className="w-full"><tbody>{inProgress.map(s => <Row key={s.id} s={s} />)}</tbody></table>
          </div>
        </div>
      )}

      <div>
        <div className="text-[9px] font-bold text-gray-500 uppercase mb-1">Recent</div>
        <div className="bg-white border border-gray-200 rounded overflow-x-auto">
          <table className="w-full"><tbody>
            {closed.map(s => <Row key={s.id} s={s} />)}
            {closed.length === 0 && inProgress.length === 0 && (
              <tr><td className="text-center py-8 text-[10px] text-gray-400">No stock takes yet — start one to begin.</td></tr>
            )}
          </tbody></table>
        </div>
      </div>

      {showStart && (
        <Modal title="Start Stock Take" onClose={() => setShowStart(false)} footer={
          <><button onClick={() => setShowStart(false)} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
            <button onClick={start} disabled={!startLoc || starting} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">{starting ? 'Starting…' : 'Start'}</button></>
        }>
          <p className="text-[10px] text-gray-500 mb-3">Pick a location. Every non-decommissioned item currently assigned to it will be listed for counting.</p>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Location *</label>
          <select className="form-select" value={startLoc} onChange={e => setStartLoc(e.target.value)}>
            <option value="">Select location…</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </Modal>
      )}
    </div>
  );
}

// The running stock-take view: outstanding (left) vs confirmed (right), scan/confirm,
// complete, and act on whatever's missing. Reused full-screen by the ?stocktake= scan page.
export function StockTakeSession({ id, sows, canManage, onBack, scanMode = false }) {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const [qrUrl, setQrUrl] = useState('');
  const [missingAction, setMissingAction] = useState(null); // status string for BulkStatusModal
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`stock-takes/${id}`).then(setData).catch(() => {}), [id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const url = `${window.location.origin}/?stocktake=${id}`;
    QRCode.toDataURL(url, { width: 150, margin: 1, color: { dark: '#000000', light: '#ffffff' } }).then(setQrUrl).catch(() => {});
  }, [id]);

  if (!data) return <div className="text-[10px] text-gray-400 py-8 text-center">Loading…</div>;

  const items = data.items || [];
  const confirmed = items.filter(i => i.state === 'confirmed');
  const pending = items.filter(i => i.state === 'pending');
  const pct = items.length ? Math.round((confirmed.length / items.length) * 100) : 0;
  const isOpen = data.status === 'in_progress';
  const stockUrl = `${window.location.origin}/?stocktake=${id}`;

  const confirmItem = async (itemId, method = 'click') => {
    try { await api.post(`stock-takes/${id}/confirm`, { item_id: itemId, method }); load(); }
    catch (e) { toast(e?.data?.error || e.message || 'Failed', 'error'); }
  };
  const unconfirm = async (itemId) => { try { await api.post(`stock-takes/${id}/unconfirm`, { item_id: itemId }); load(); } catch (e) { /* ignore */ } };
  const confirmTag = async () => {
    // Accept a raw asset tag or a full scanned URL (…?scan=AV-INV-00001)
    let tag = tagInput.trim();
    const m = tag.match(/[?&]scan=([^&]+)/i);
    if (m) tag = decodeURIComponent(m[1]);
    if (!tag) return;
    try {
      await api.post(`stock-takes/${id}/confirm`, { asset_tag: tag, method: 'scan' });
      toast(`Confirmed ${tag}`, 'success'); setTagInput(''); load();
    } catch (e) { toast(e?.data?.error || `${tag}: ${e.message}`, 'error'); setTagInput(''); }
  };
  const complete = async () => {
    if (!window.confirm(`Complete this stock take? ${pending.length} item(s) will be flagged as missing.`)) return;
    setBusy(true);
    try { await api.post(`stock-takes/${id}/complete`, {}); load(); } catch (e) { toast(e?.data?.error || e.message, 'error'); }
    setBusy(false);
  };

  const missingItems = pending.map(i => ({ id: i.item_id, asset_tag: i.asset_tag, name: i.name, status: i.current_status }));

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        {onBack && <button onClick={onBack} className="px-3 py-1.5 border border-gray-200 text-[10px] rounded hover:bg-gray-50 text-gray-600">← Back</button>}
        <div>
          <div className="text-[12px] font-extrabold text-navy">{data.location_name || 'Stock Take'}</div>
          <div className="text-[8px] text-gray-400">Started by {data.started_by_name || '—'} · <span className={`font-semibold ${isOpen ? 'text-sky-600' : 'text-gray-500'}`}>{isOpen ? 'In progress' : data.status === 'completed' ? 'Completed' : 'Cancelled'}</span></div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {isOpen && canManage && (
            <button onClick={complete} disabled={busy} className="px-3 py-1.5 text-[10px] font-bold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-40">
              Complete ({pending.length} missing)
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-3">
        <div className="flex justify-between text-[9px] text-gray-500 mb-1">
          <span><strong className="text-emerald-600">{confirmed.length}</strong> confirmed · <strong className="text-amber-600">{pending.length}</strong> outstanding</span>
          <span>{pct}% of {items.length}</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Scan / confirm input + QR */}
      {isOpen && (
        <div className="flex gap-3 mb-3 flex-wrap items-start">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Scan or type an asset tag</label>
            <div className="flex gap-2">
              <input autoFocus className="form-input flex-1" value={tagInput} placeholder="AV-INV-00001"
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') confirmTag(); }} />
              <button onClick={confirmTag} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded">Confirm</button>
            </div>
            {!scanMode && (
              <div className="mt-2 text-[8px] text-gray-400">
                On a phone, open the stock-take URL to scan item QR labels:
                <button onClick={() => { navigator.clipboard?.writeText(stockUrl); toast('Copied', 'success'); }} className="ml-1 text-gold hover:underline break-all">{stockUrl}</button>
              </div>
            )}
          </div>
          {!scanMode && qrUrl && (
            <div className="text-center">
              <img src={qrUrl} alt="Stock take QR" className="w-[110px] h-[110px] border border-gray-200 rounded" />
              <div className="text-[7px] text-gray-400 mt-0.5">Scan to open on phone</div>
            </div>
          )}
        </div>
      )}

      {/* Missing actions (after completion, or anytime for admins) */}
      {!isOpen && pending.length > 0 && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded flex-wrap">
          <span className="text-[10px] font-bold text-amber-800">{pending.length} item{pending.length > 1 ? 's' : ''} not found</span>
          <span className="text-[9px] text-amber-600">— act on the missing stock:</span>
          {canManage && <>
            <button onClick={() => setMissingAction('decommissioned')} className="px-2 py-1 text-[9px] font-semibold bg-gray-500 text-white rounded hover:bg-gray-600">🗑 Decommission</button>
            <button onClick={() => setMissingAction('in_transit')} className="px-2 py-1 text-[9px] font-semibold bg-sky-500 text-white rounded hover:bg-sky-600">🚚 Mark In Transit</button>
          </>}
        </div>
      )}

      {/* Two columns: outstanding (left) · confirmed (right) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white border border-gray-200 rounded">
          <div className="px-3 py-1.5 bg-amber-50 border-b border-amber-100 text-[9px] font-bold text-amber-700 uppercase sticky top-0">Outstanding ({pending.length})</div>
          <div className="max-h-[52vh] overflow-y-auto divide-y divide-gray-100">
            {pending.map(i => (
              <div key={i.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-amber-50/40">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-mono font-semibold text-navy">{i.asset_tag || '—'}</div>
                  <div className="text-[9px] text-gray-600 truncate">{i.name}{i.serial_number ? ` · S/N ${i.serial_number}` : ''}</div>
                </div>
                {isOpen
                  ? <button onClick={() => confirmItem(i.item_id)} className="px-2 py-1 text-[9px] font-semibold bg-emerald-500 text-emerald-950 rounded hover:bg-emerald-600 shrink-0">✓ Confirm</button>
                  : <span className="text-[8px] font-semibold text-red-500 shrink-0">Missing</span>}
              </div>
            ))}
            {pending.length === 0 && <div className="text-center py-8 text-[10px] text-emerald-600">All items confirmed 🎉</div>}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded">
          <div className="px-3 py-1.5 bg-emerald-50 border-b border-emerald-100 text-[9px] font-bold text-emerald-700 uppercase sticky top-0">Confirmed ({confirmed.length})</div>
          <div className="max-h-[52vh] overflow-y-auto divide-y divide-gray-100">
            {confirmed.map(i => (
              <div key={i.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-emerald-50/40">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-mono font-semibold text-gray-700">{i.asset_tag || '—'}</div>
                  <div className="text-[9px] text-gray-500 truncate">{i.name} <span className="text-gray-300">· {i.method === 'scan' ? 'scanned' : 'confirmed'}</span></div>
                </div>
                {isOpen && <button onClick={() => unconfirm(i.item_id)} className="text-[8px] text-gray-400 hover:text-red-500 shrink-0">undo</button>}
              </div>
            ))}
            {confirmed.length === 0 && <div className="text-center py-8 text-[10px] text-gray-400">Nothing confirmed yet</div>}
          </div>
        </div>
      </div>

      {missingAction && (
        <BulkStatusModal items={missingItems} status={missingAction} sows={sows}
          onClose={() => setMissingAction(null)}
          onDone={() => { setMissingAction(null); load(); }} />
      )}
    </div>
  );
}

// ─── LOCATIONS VIEW ──────────────────────────────────────────────────────────

function LocationsView({ locations, onRefresh, canManage }) {
  const { toast } = useToast();
  const [showAdd, setShowAdd] = useState(false);
  const [editLoc, setEditLoc] = useState(null);
  const [rows, setRows] = useState(locations);     // local list — includes inactive
  const [deletingId, setDeletingId] = useState(null);

  // Load the full list (including inactive) so inactive locations can be deleted.
  // The shared `locations` prop stays active-only for item-assignment dropdowns.
  const loadLocal = useCallback(() => {
    api.get('inventory/locations', { include_inactive: 1 }).then(setRows).catch(() => {});
  }, []);
  useEffect(() => { loadLocal(); }, [loadLocal]);

  const refreshAll = () => { loadLocal(); onRefresh(); };

  const activeCount = rows.filter(l => l.is_active !== 0).length;
  const inactiveCount = rows.length - activeCount;
  const total_in_stock = rows.reduce((s, l) => s + (l.in_stock_count || 0), 0);
  const total_items = rows.reduce((s, l) => s + (l.item_count || 0), 0);
  const total_value = rows.reduce((s, l) => s + (l.total_value || 0), 0);
  const seesCost = canAccess('trade-cost:see');

  const handleDelete = async (l) => {
    if (!window.confirm(`Permanently delete the empty location "${l.name}"? This cannot be undone.`)) return;
    setDeletingId(l.id);
    try {
      await api.delete(`inventory/locations/${l.id}`);
      toast(`Deleted "${l.name}"`, 'success');
      refreshAll();
    } catch (e) {
      toast(e?.data?.error || e.message || 'Could not delete location', 'error');
    }
    setDeletingId(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] font-semibold text-gray-600">
            {activeCount} active{inactiveCount > 0 ? ` · ${inactiveCount} inactive` : ''} locations · {total_items} items tracked · {total_in_stock} in stock · {seesCost && <><span className="text-emerald-600">{formatMoney(total_value)}</span> total value</>}
          </div>
          <div className="text-[8px] text-gray-400">Value = catalogue unit cost (or purchase price) × quantity, excluding decommissioned. Useful for insurance.</div>
        </div>
        {canManage && (
          <button onClick={() => setShowAdd(true)} className="px-3 py-1.5 text-[10px] font-semibold bg-navy text-white rounded hover:bg-navy/80">+ Add Location</button>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded overflow-x-auto">
        <table className="w-full">
          <thead><tr>
            {['Location','Type','In Stock','Incoming','Total Items','Value', canManage ? 'Actions' : ''].filter(Boolean).map(h => (
              <th key={h} className={`px-2 py-1.5 text-[8px] uppercase font-bold text-gray-500 border-b bg-gray-50 ${h==='Actions'||h==='Value'||h===''?'text-right':'text-left'}`}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={canManage ? 7 : 6} className="text-center py-8 text-[10px] text-gray-400">No locations configured</td></tr>}
            {rows.map(l => {
              const isInactive = l.is_active === 0;
              const isEmpty = (l.item_count || 0) === 0;
              return (
              <tr key={l.id} className={`border-b border-gray-100 hover:bg-gray-50 ${isInactive ? 'opacity-60' : ''}`}>
                <td className="px-2 py-1.5">
                  <div className="text-[10px] font-semibold flex items-center gap-1.5">
                    {l.name}
                    {isInactive && <span className="text-[7px] uppercase font-bold bg-gray-200 text-gray-500 px-1 py-0.5 rounded">Inactive</span>}
                  </div>
                  {l.address && <div className="text-[8px] text-gray-400">{l.address}</div>}
                </td>
                <td className="px-2 py-1.5"><span className="text-[9px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded capitalize">{l.type || 'storage'}</span></td>
                <td className="px-2 py-1.5 text-[10px] font-bold text-emerald-600">{l.in_stock_count || 0}</td>
                <td className="px-2 py-1.5 text-[10px] font-bold text-purple-600">{l.incoming_count || 0}</td>
                <td className="px-2 py-1.5 text-[10px] font-bold text-navy">{l.item_count || 0}</td>
                <td className="px-2 py-1.5 text-[10px] font-bold text-emerald-600 text-right whitespace-nowrap">{seesCost ? formatMoney(l.total_value || 0) : '—'}</td>
                {canManage && (
                  <td className="px-2 py-1.5 text-right whitespace-nowrap">
                    <button onClick={() => setEditLoc(l)} className="text-[9px] text-gold hover:underline">Edit</button>
                    {isInactive && isEmpty && (
                      <button onClick={() => handleDelete(l)} disabled={deletingId === l.id}
                        className="text-[9px] text-red-600 hover:underline ml-3 disabled:opacity-40">
                        {deletingId === l.id ? 'Deleting…' : 'Delete'}
                      </button>
                    )}
                    {isInactive && !isEmpty && (
                      <span className="text-[8px] text-gray-400 ml-3" title="Move or remove its stock before it can be deleted">Has stock</span>
                    )}
                  </td>
                )}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {(showAdd || editLoc) && (
        <LocationModal
          location={editLoc}
          onClose={() => { setShowAdd(false); setEditLoc(null); }}
          onSave={async (data) => {
            if (editLoc) {
              await api.put(`inventory/locations/${editLoc.id}`, data);
            } else {
              await api.post('inventory/locations', data);
            }
            setShowAdd(false);
            setEditLoc(null);
            refreshAll();
          }}
        />
      )}
    </div>
  );
}

// ─── LOCATION MODAL ──────────────────────────────────────────────────────────

function LocationModal({ location, onClose, onSave }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: location?.name || '', type: location?.type || 'storage', address: location?.address || '', contact: location?.contact || '', is_active: location?.is_active ?? 1 });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handle = async () => {
    if (!form.name.trim()) { toast('Location name required', 'error'); return; }
    setSaving(true);
    try { await onSave(form); }
    catch (e) { toast(e.message || 'Failed', 'error'); setSaving(false); }
  };

  return (
    <Modal title={location ? 'Edit Location' : 'Add Storage Location'} onClose={onClose} footer={
      <><button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={handle} disabled={saving} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">{saving ? 'Saving…' : 'Save'}</button></>
    }>
      <div className="grid grid-cols-2 gap-2">
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Name *</label>
          <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Warehouse A, Shelf B4" autoFocus />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Type</label>
          <select className="form-select" value={form.type} onChange={e => set('type', e.target.value)}>
            <option value="storage">Storage</option>
            <option value="warehouse">Warehouse</option>
            <option value="rack">Rack</option>
            <option value="shelf">Shelf</option>
            <option value="office">Office</option>
            <option value="client_site">Client Site</option>
            <option value="transit">In Transit</option>
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Contact</label>
          <input className="form-input" value={form.contact} onChange={e => set('contact', e.target.value)} placeholder="e.g. John Smith" />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Address</label>
          <input className="form-input" value={form.address} onChange={e => set('address', e.target.value)} placeholder="e.g. Unit 5, Industrial Estate" />
        </div>
        {location && (
          <div className="col-span-2 flex items-center gap-2">
            <input type="checkbox" id="loc-active" checked={form.is_active === 1} onChange={e => set('is_active', e.target.checked ? 1 : 0)} />
            <label htmlFor="loc-active" className="text-[9px] text-gray-600">Location is active</label>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── ITEM DETAIL MODAL ───────────────────────────────────────────────────────

function ItemDetailModal({ item: initialItem, locations, sows, canManage, onClose, onUpdate, onRefreshItem, onRecordsChanged }) {
  const { toast } = useToast();
  const [item, setItem] = useState(initialItem);
  const [showHistory, setShowHistory] = useState(false);
  const [allocSowId, setAllocSowId] = useState('');
  const [allocNotes, setAllocNotes] = useState('');
  const [pendingStatus, setPendingStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({
    name: item.name || '', serial_number: item.serial_number || '', part_number: item.part_number || '',
    category: item.category || '', description: item.description || '', manufacturer: item.manufacturer || '',
    purchase_price: item.purchase_price ?? '', location_id: item.location_id || '', notes: item.notes || '',
    condition_state: item.condition_state || 'good',
  });
  const [quickLocId, setQuickLocId] = useState(item.location_id || '');
  // Splitting a record of several units into one per unit: asked inline, then
  // the new tags are listed here with their labels, where the person acted.
  const [splitAsk, setSplitAsk] = useState(false);
  const [splitDone, setSplitDone] = useState(null);
  // The count of a batch record, stepped with − and + or typed, then saved
  // with a reason. Offered for a record opened holding several units, and
  // kept while the dialog is open even if the count is brought down to one.
  const [isBatch] = useState((initialItem.quantity || 1) > 1);
  const [countDraft, setCountDraft] = useState(String(initialItem.quantity || 1));
  const [countReason, setCountReason] = useState('');
  const [countNote, setCountNote] = useState(null); // { ok, text }

  const update = async (patch) => {
    setSaving(true);
    try {
      await onUpdate(item.id, patch);
      const fresh = await api.get(`inventory/${item.id}`);
      if (fresh?.id) { setItem(fresh); setEditForm(f => ({ ...f, location_id: fresh.location_id || '', notes: fresh.notes || '' })); setQuickLocId(fresh.location_id || ''); }
    } catch (e) { toast(e.message || 'Update failed', 'error'); }
    finally { setSaving(false); }
  };

  const doTransition = (newStatus) => {
    if (newStatus === 'allocated' || newStatus === 'deployed') {
      setPendingStatus(newStatus);
      // Deploying an already-allocated item defaults to the SOW it's allocated
      // to (the end user can still change it); allocating starts blank.
      setAllocSowId(newStatus === 'deployed' ? (item.allocated_to_sow_id || '') : '');
      setAllocNotes('');
    } else update({ status: newStatus });
  };

  const confirmTransition = () => {
    if (!allocSowId && !allocNotes.trim()) { toast('Link to a SOW or enter a reason', 'error'); return; }
    update({ status: pendingStatus, allocated_to_sow_id: allocSowId || null, notes: allocNotes || null });
    setPendingStatus(null);
  };

  const unallocate = () => update({ status: 'in_stock', allocated_to_sow_id: null });

  const saveEdit = () => {
    const patch = { ...editForm, purchase_price: editForm.purchase_price !== '' ? parseFloat(editForm.purchase_price) : null, location_id: editForm.location_id || null };
    update(patch).then(() => setShowEdit(false));
  };

  const handleQuickLocChange = (locId) => {
    setQuickLocId(locId);
    update({ location_id: locId || null });
  };

  const doSplit = async () => {
    setSaving(true);
    try {
      const r = await api.post(`inventory/${item.id}/split`, {});
      const fresh = await api.get(`inventory/${item.id}`);
      if (fresh?.id) setItem(fresh);
      setSplitDone(r.items || []);
      setSplitAsk(false);
      onRecordsChanged?.();
    } catch (e) { toast(e.message || 'Split failed', 'error'); }
    finally { setSaving(false); }
  };

  const saveCount = async () => {
    const to = commitQuantity(countDraft, { min: 1, max: COUNT_MAX });
    setSaving(true);
    try {
      const r = await api.post(`inventory/${item.id}/adjust`, { quantity: to, expected: item.quantity || 1, reason: countReason.trim() || null });
      const fresh = await api.get(`inventory/${item.id}`);
      if (fresh?.id) setItem(fresh);
      setCountDraft(String(r.quantity));
      setCountReason('');
      setCountNote({ ok: true, text: `Count saved: ${r.previous} → ${r.quantity}` });
      onRecordsChanged?.();
    } catch (e) {
      // Changed by somebody else since the dialog opened: show what it holds now.
      if (e?.data?.quantity != null) {
        setItem(it => ({ ...it, quantity: e.data.quantity }));
        setCountDraft(String(e.data.quantity));
      }
      setCountNote({ ok: false, text: e?.data?.error || e.message || 'Count not saved' });
    } finally { setSaving(false); }
  };

  // The parts share everything but the tag and the serial, so their labels are
  // the original's with the new tag on.
  const printNewLabels = () => printLabelsBatch(
    splitDone.slice(1).map(p => ({ ...item, id: p.id, asset_tag: p.asset_tag, serial_number: null, quantity: 1 })),
    (msg) => toast(msg, 'error'),
  );

  const qty = item.quantity || 1;
  const countTo = commitQuantity(countDraft, { min: 1, max: COUNT_MAX });
  const countDelta = countTo - qty;
  const countable = canManage && isBatch && !splitDone && !countProblem(item);
  const shared = ['status', 'location',
    item.allocated_sow_reference && `SOW ${item.allocated_sow_reference}`,
    item.source_po_number && `PO ${item.source_po_number}`].filter(Boolean);
  const sharedWith = `${shared.slice(0, -1).join(', ')} and ${shared[shared.length - 1]}`;
  const transitions = VALID_TRANSITIONS[item.status] || [];
  const isAllocated = item.status === 'allocated' || item.status === 'deployed';

  return (
    <Modal title={item.asset_tag || 'Item Detail'} onClose={onClose} width="max-w-lg"
      footer={
        <div className="flex gap-2 items-center">
          <button onClick={() => printLabel(item, (msg) => toast(msg, 'error'))} className="px-3 py-1 text-[10px] font-semibold border border-gray-300 rounded hover:bg-gray-50">🖨 Print Label</button>
          {/* Status and location above are scanning; editing the item's details is managing stock. */}
          {canManage && <button onClick={() => setShowEdit(v => !v)} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">{showEdit ? 'Cancel Edit' : 'Edit Item'}</button>}
          <button onClick={onClose} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded">Close</button>
        </div>
      }>

      {/* Identity */}
      <div className="mb-3">
        <div className="text-[18px] font-extrabold font-mono text-navy">{item.asset_tag || '—'}</div>
        {item.serial_number && <div className="text-[10px] text-gray-500 font-mono">S/N: {item.serial_number}</div>}
        <div className="text-[11px] font-semibold mt-0.5">{item.name}</div>
        {(item.quantity || 1) > 1 && (
          <div className="flex items-center gap-2 mt-0.5">
            <div className="text-[10px] font-bold text-navy">
              {item.quantity} units <span className="font-normal text-gray-500">in this record</span>
            </div>
            {canManage && !splitAsk && qty <= 100 && (
              <button onClick={() => setSplitAsk(true)} disabled={saving} data-testid="split-item"
                className="px-2 py-0.5 text-[8px] font-semibold border border-gray-300 rounded bg-white hover:bg-gray-50">
                Split into {qty} individual records
              </button>
            )}
            {canManage && qty > 100 && <span className="text-[8px] text-gray-400">Over 100 units, too many to split</span>}
          </div>
        )}
        {(item.catalog_manufacturer || item.manufacturer) && <div className="text-[9px] text-gray-400">{item.catalog_manufacturer || item.manufacturer}</div>}
      </div>

      {countable && !splitAsk && (
        <div className="mb-3 border border-gray-200 rounded p-2" data-testid="count-adjust">
          <div className="flex items-center gap-1.5">
            <span className="text-[8px] font-bold text-gray-500 uppercase mr-1">Count</span>
            <button onClick={() => { setCountNote(null); setCountDraft(String(Math.max(1, countTo - 1))); }} disabled={saving || countTo <= 1}
              aria-label="One fewer" className="w-7 h-7 flex-none flex items-center justify-center p-0 text-[13px] font-bold border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-40">−</button>
            <input className="w-16 text-center text-[11px] font-semibold px-1 py-1 border border-gray-300 rounded bg-white" inputMode="numeric" aria-label="Units in this record"
              value={countDraft} disabled={saving}
              onChange={e => { setCountNote(null); setCountDraft(sanitiseQuantityInput(e.target.value)); }}
              onBlur={() => setCountDraft(String(countTo))} />
            <button onClick={() => { setCountNote(null); setCountDraft(String(Math.min(COUNT_MAX, countTo + 1))); }} disabled={saving || countTo >= COUNT_MAX}
              aria-label="One more" className="w-7 h-7 flex-none flex items-center justify-center p-0 text-[13px] font-bold border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-40">+</button>
            {countDelta !== 0 && (
              <span className="text-[9px] text-gray-600 ml-1 whitespace-nowrap">{qty} → <strong>{countTo}</strong> <span className={countDelta > 0 ? 'text-emerald-700' : 'text-red-600'}>({countDelta > 0 ? '+' : ''}{countDelta})</span></span>
            )}
          </div>
          {countDelta !== 0 && (
            <div className="flex gap-1 mt-1.5">
              <input className="form-input flex-1 text-[10px]" value={countReason} onChange={e => setCountReason(e.target.value)} disabled={saving}
                placeholder="Reason (optional), e.g. found 2 more in the cage" />
              <button onClick={saveCount} disabled={saving} className="px-2 py-1 text-[8px] font-semibold bg-navy text-white rounded disabled:opacity-40 whitespace-nowrap">
                {saving ? 'Saving…' : `Save count of ${countTo}`}
              </button>
              <button onClick={() => { setCountDraft(String(qty)); setCountReason(''); }} disabled={saving} className="px-2 py-1 text-[8px] border border-gray-200 rounded bg-white whitespace-nowrap">Keep {qty}</button>
            </div>
          )}
          {countNote && <div className={`text-[8px] mt-1 ${countNote.ok ? 'text-emerald-700' : 'text-red-600'}`}>{countNote.text}</div>}
          {countTo === 1 && countDelta < 0 && <div className="text-[8px] text-gray-400 mt-1">One is the lowest a count goes. To take the last unit out, decommission the record.</div>}
        </div>
      )}

      {splitAsk && (
        <div className="mb-3 bg-blue-50 border border-blue-200 rounded p-2 space-y-1.5">
          <div className="text-[9px] font-bold text-blue-700">Split {item.asset_tag} into {qty} records of one unit each?</div>
          <div className="text-[9px] text-gray-600">
            {item.asset_tag} keeps one unit and its label. {qty === 2 ? 'One new record gets the next asset tag' : `${qty - 1} new records get the next asset tags`},
            with the same {sharedWith}.
            Each new record needs its own label, which you can print straight after.
          </div>
          <div className="flex gap-1">
            <button onClick={doSplit} disabled={saving} className="px-2 py-1 text-[8px] font-semibold bg-navy text-white rounded disabled:opacity-40">
              {saving ? 'Splitting…' : `Split into ${qty}`}
            </button>
            <button onClick={() => setSplitAsk(false)} disabled={saving} className="px-2 py-1 text-[8px] border border-gray-200 rounded bg-white">Keep as one record</button>
          </div>
        </div>
      )}

      {splitDone && splitDone.length > 1 && (
        <div className="mb-3 bg-emerald-50 border border-emerald-200 rounded p-2 space-y-1">
          <div className="text-[9px] font-bold text-emerald-700">Split into {splitDone.length} records</div>
          <div className="text-[9px] font-mono text-gray-700 break-words">{splitDone.map(p => p.asset_tag).join(', ')}</div>
          <div className="text-[8px] text-gray-500">{splitDone[0].asset_tag} keeps its label. The other {splitDone.length - 1} need one each.</div>
          <button onClick={printNewLabels} className="px-2 py-1 text-[8px] font-semibold border border-gray-300 rounded bg-white hover:bg-gray-50">
            🖨 Print {splitDone.length - 1} new label{splitDone.length - 1 === 1 ? '' : 's'}
          </button>
        </div>
      )}

      {/* Quick location change */}
      <div className="mb-3 flex items-center gap-2">
        <label className="text-[8px] font-bold text-gray-500 uppercase whitespace-nowrap">Location:</label>
        <select className="form-select text-[10px] flex-1" value={quickLocId} onChange={e => handleQuickLocChange(e.target.value)} disabled={saving}>
          <option value="">(No location)</option>
          {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>

      {/* Catalog link */}
      {item.product_catalog_id ? (
        <div className="mb-3 bg-blue-50 border border-blue-200 rounded p-2 text-[9px]">
          <div className="font-bold text-blue-700">Linked to product catalogue</div>
          {item.catalog_description && <div className="text-gray-600">{item.catalog_description}</div>}
          {item.catalog_unit_cost != null && <div className="text-emerald-700 font-semibold mt-0.5">Unit cost: {formatMoney(item.catalog_unit_cost)}</div>}
        </div>
      ) : (
        <div className="mb-3 bg-gray-50 border border-gray-200 rounded p-2 text-[9px] text-gray-400">Not linked to product catalogue</div>
      )}

      {/* Incoming ETA */}
      {item.status === 'incoming' && (
        <div className="mb-3 bg-purple-50 border border-purple-200 rounded p-2 text-[9px]">
          <div className="font-bold text-purple-700">Incoming</div>
          {item.source_po_number && <div>PO: <span className="font-semibold">{item.source_po_number}</span></div>}
          <div>ETA: <span className="font-semibold">{item.expected_delivery ? formatDate(item.expected_delivery) : 'Not set'}</span></div>
        </div>
      )}

      {/* Allocation */}
      {isAllocated && (item.allocated_sow_reference || item.notes) && (
        <div className="mb-3 bg-amber-50 border border-amber-200 rounded p-2 text-[9px]">
          <div className="font-bold text-amber-700">{item.status === 'deployed' ? '🚀 Deployed' : '📦 Allocated'}</div>
          {item.allocated_sow_reference && <div>SOW: <span className="font-semibold">{item.allocated_sow_reference} - {item.allocated_sow_detail}</span></div>}
          {item.notes && <div className="text-gray-600 mt-0.5">{item.notes}</div>}
          <button onClick={unallocate} disabled={saving} className="mt-1 px-2 py-0.5 text-[8px] bg-white border border-amber-300 rounded text-amber-700 hover:bg-amber-100">Unallocate → In Stock</button>
        </div>
      )}

      {/* Status & transitions */}
      <div className="mb-3">
        <div className="flex items-center gap-2 mb-1.5">
          <span className={`text-[9px] font-semibold px-2 py-0.5 rounded ${STATUS_COLORS[item.status]}`}>{STATUS_LABELS[item.status]}</span>
          {item.purchase_price != null && <span className="text-[9px] text-gray-500">{formatMoney(item.purchase_price)}</span>}
        </div>
        {!pendingStatus && transitions.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {transitions.map(t => (
              <button key={t} disabled={saving} onClick={() => doTransition(t)}
                className={`px-2 py-1 text-[8px] font-semibold rounded transition-colors ${TRANSITION_COLORS[t]}`}>
                {TRANSITION_LABELS[t]}
              </button>
            ))}
          </div>
        )}
        {pendingStatus && (
          <div className="mt-2 bg-amber-50 border border-amber-200 rounded p-2 space-y-1.5">
            <div className="text-[9px] font-bold text-amber-700">{TRANSITION_LABELS[pendingStatus]} — Select SOW or enter reason</div>
            <SowSelect sows={sows} value={allocSowId} onChange={setAllocSowId} placeholder="Search for a SOW…" inputClassName="form-select text-[10px] w-full" />
            {pendingStatus === 'deployed' && allocSowId && allocSowId === item.allocated_to_sow_id && (
              <div className="text-[8px] text-amber-600">Defaulted to the allocated SOW — change it above if deploying elsewhere.</div>
            )}
            {!allocSowId && <input className="form-input text-[10px]" placeholder="Reason (required if no SOW)" value={allocNotes} onChange={e => setAllocNotes(e.target.value)} />}
            <div className="flex gap-1">
              <button onClick={confirmTransition} disabled={saving} className="px-2 py-1 text-[8px] font-semibold bg-navy text-white rounded disabled:opacity-40">Confirm</button>
              <button onClick={() => setPendingStatus(null)} className="px-2 py-1 text-[8px] border border-gray-200 rounded">Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* Activity history */}
      <div className="mb-2">
        <button onClick={() => setShowHistory(v => !v)} className="text-[9px] font-bold text-gray-500 hover:text-gray-700">
          {showHistory ? '▲ Hide' : '▼ Show'} Activity History
        </button>
        {showHistory && <ItemHistory itemId={item.id} />}
      </div>

      {/* Edit form */}
      {showEdit && canManage && (
        <div className="mt-3 border-t border-gray-100 pt-3">
          <div className="text-[9px] font-bold text-gray-600 mb-2 uppercase tracking-wide">Edit Item Details</div>
          <div className="grid grid-cols-2 gap-1.5">
            <div className="col-span-2"><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Name</label><input className="form-input" value={editForm.name} onChange={e => setEditForm(f=>({...f,name:e.target.value}))} /></div>
            <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Serial Number</label><input className="form-input" value={editForm.serial_number} onChange={e => setEditForm(f=>({...f,serial_number:e.target.value}))} /></div>
            <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Category</label><input className="form-input" value={editForm.category} onChange={e => setEditForm(f=>({...f,category:e.target.value}))} /></div>
            <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Manufacturer</label><input className="form-input" value={editForm.manufacturer} onChange={e => setEditForm(f=>({...f,manufacturer:e.target.value}))} /></div>
            <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Purchase Price</label><input className="form-input" type="number" step="0.01" value={editForm.purchase_price} onChange={e => setEditForm(f=>({...f,purchase_price:e.target.value}))} /></div>
            <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Condition</label>
              <select className="form-select" value={editForm.condition_state} onChange={e => setEditForm(f=>({...f,condition_state:e.target.value}))}>
                {['new','good','fair','poor','damaged'].map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
              </select>
            </div>
            <div className="col-span-2"><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Notes</label><textarea className="form-textarea" rows="2" value={editForm.notes} onChange={e => setEditForm(f=>({...f,notes:e.target.value}))} /></div>
          </div>
          <div className="flex gap-2 mt-2">
            <button onClick={saveEdit} disabled={saving} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">{saving?'Saving…':'Save Changes'}</button>
            <button onClick={() => setShowEdit(false)} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Label helpers ─────────────────────────────────────────────────────────────

function escHtml(str) {
  if (str == null) return '';
  const d = document.createElement('div');
  d.textContent = String(str);
  return d.innerHTML;
}

// ── Print label (Brother DK-22211: 29mm wide, continuous) ─────────────────────

// Per-item label fields, derived identically for single and batch printing so
// both routes produce the same label.
function labelFields(item) {
  const assetTag = item.asset_tag || '';
  return {
    assetTag,
    name: item.catalog_description || item.name || '',
    serial: item.serial_number || '',
    manufacturer: item.catalog_manufacturer || item.manufacturer || '',
    eta: item.status === 'incoming' && item.expected_delivery
      ? `ETA ${new Date(item.expected_delivery).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
      : '',
    scanUrl: `https://ops.altonvalley.com/?scan=${encodeURIComponent(assetTag)}`,
    updated: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
  };
}

// Shared label styling. `.label` carries the per-label padding and (in batch
// printing) the page break, so each label always prints on its own form.
const LABEL_CSS = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { font-family: Arial, Helvetica, sans-serif; background: #fff; color: #000; }
  .label { width: 29mm; max-width: 29mm; padding: 2mm 1.5mm 1.5mm; }
  .logo { display: block; width: 16mm; height: auto; margin: 0 auto 1mm; }
  .divider { border-top: 0.3mm solid #000; margin: 0.8mm 0; }
  .tag { font-family: 'Courier New', monospace; font-size: 6.5mm; font-weight: 900; letter-spacing: 0.3mm; text-align: center; line-height: 1; margin: 0.8mm 0; }
  .name { font-size: 2.6mm; font-weight: 700; text-align: center; line-height: 1.2; word-break: break-word; margin: 0.5mm 0; }
  .detail { font-size: 1.9mm; text-align: center; color: #555; line-height: 1.3; margin: 0.2mm 0; }
  .eta { font-size: 2mm; font-weight: 700; text-align: center; background: #000; color: #fff; padding: 0.4mm 1mm; margin: 0.8mm 0; border-radius: 0.5mm; }
  .qr { display: block; width: 19mm; height: 19mm; margin: 1mm auto; }
  .url { font-size: 1.4mm; text-align: center; color: #aaa; word-break: break-all; }
  .updated { font-size: 1.6mm; text-align: center; color: #888; margin-top: 0.5mm; }
`;

// Inner markup of one label (without the `.label` wrapper), shared by single
// and batch printing.
function labelInnerHtml(item, qrDataUrl, logoSrc) {
  const f = labelFields(item);
  return `
  ${logoSrc ? `<img src="${logoSrc}" class="logo" alt="AV" />` : ''}
  <div class="divider"></div>
  <div class="tag">${escHtml(f.assetTag)}</div>
  <div class="divider"></div>
  <div class="name">${escHtml(f.name)}</div>
  ${f.manufacturer ? `<div class="detail">${escHtml(f.manufacturer)}</div>` : ''}
  ${f.serial ? `<div class="detail">S/N: ${escHtml(f.serial)}</div>` : ''}
  ${f.eta ? `<div class="eta">${escHtml(f.eta)}</div>` : ''}
  <img src="${qrDataUrl}" class="qr" alt="QR" />
  <div class="url">${escHtml(f.scanUrl)}</div>
  <div class="updated">Updated: ${escHtml(f.updated)}</div>`;
}

function genLabelQr(value) {
  return QRCode.toDataURL(value, { width: 120, margin: 1, color: { dark: '#000000', light: '#ffffff' } });
}

function loadLabelLogo() {
  return fetch('/av-logo-colour.svg')
    .then(r => r.text())
    .then(svg => 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg))))
    .catch(() => '');
}

function printLabel(item, onError) {
  Promise.all([genLabelQr(labelFields(item).scanUrl), loadLabelLogo()])
    .then(([qrDataUrl, logoSrc]) => {
      const win = window.open('', '_blank', 'width=200,height=600');
      if (!win) { onError?.('Pop-up blocked — allow pop-ups for ops.altonvalley.com to print labels'); return; }
      win.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Label — ${escHtml(item.asset_tag || '')}</title>
  <style>
    @page { size: 29mm 90mm; margin: 0; }
    ${LABEL_CSS}
    .print-btn { display: block; width: 100%; margin-top: 3mm; padding: 1.5mm; background: #A1723D; color: #fff; border: none; border-radius: 1mm; font-size: 2.8mm; cursor: pointer; font-family: Arial, sans-serif; }
    @media print { .print-btn { display: none; } }
  </style>
</head>
<body>
  <div class="label">${labelInnerHtml(item, qrDataUrl, logoSrc)}</div>
  <button class="print-btn" type="button">🖨 Print</button>
</body>
</html>`);
      win.document.close();
      // Wired from here rather than an inline onclick: the pop-up inherits the
      // app's CSP, which refuses inline script (pen test AD25996).
      win.document.querySelector('.print-btn')?.addEventListener('click', () => win.print());
      win.onload = () => { win.focus(); win.print(); };
      setTimeout(() => { try { win.focus(); win.print(); } catch(_) {} }, 600);
    })
    .catch(() => onError?.('Could not generate label'));
}

// ── Batch print labels (chunked so the label printer is never overloaded) ─────
// Ported from the events badge batch-printer: a big selection is never sent to
// the printer as one giant job. Instead labels print in chunks (selectable
// 10/25/50, default 25): each batch prints, then the next starts automatically
// on `afterprint` — hands-free in a kiosk-printing browser, otherwise one print
// dialog per batch. "Next batch" forces the next chunk if a dialog didn't
// advance; "Stop" halts the run. A short gap between batches lets the printer
// catch up. All printing happens inside a dedicated pop-up window so it is fully
// isolated from the app's own layout.
const LABEL_BATCH_DEFAULT = 25;

function printLabelsBatch(items, onError) {
  if (!items || items.length === 0) return;
  Promise.all([
    Promise.all(items.map(i => genLabelQr(labelFields(i).scanUrl).catch(() => ''))),
    loadLabelLogo(),
  ])
    .then(([qrs, logoSrc]) => {
      const total = items.length;
      const labelsHtml = items
        .map((it, idx) => `<div class="label" data-idx="${idx}">${labelInnerHtml(it, qrs[idx], logoSrc)}</div>`)
        .join('');

      const win = window.open('', '_blank', 'width=460,height=760');
      if (!win) { onError?.('Pop-up blocked — allow pop-ups for ops.altonvalley.com to print labels'); return; }

      win.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Print ${total} label${total === 1 ? '' : 's'}</title>
  <style>
    @page { size: 29mm 90mm; margin: 0; }
    ${LABEL_CSS}
    body { background: #f3f4f6; }
    #bar { font-family: Arial, Helvetica, sans-serif; padding: 12px 14px; background: #1B2A4A; color: #fff; position: sticky; top: 0; }
    #bar h1 { font-size: 14px; margin: 0 0 8px; font-weight: 800; }
    #progress { font-size: 12px; color: #cbd5e1; margin-bottom: 10px; }
    .row { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
    .lbl { font-size: 11px; color: #94a3b8; margin-right: 2px; }
    .bs { padding: 4px 10px; font-size: 12px; border: 1px solid #475569; background: #fff; color: #1B2A4A; border-radius: 4px; cursor: pointer; }
    .bs.active { background: #A1723D; color: #fff; border-color: #A1723D; font-weight: 700; }
    .bs:disabled { opacity: 0.5; cursor: not-allowed; }
    .btn { padding: 6px 14px; font-size: 13px; font-weight: 700; border: none; border-radius: 4px; cursor: pointer; }
    .btn-go { background: #A1723D; color: #fff; }
    .btn-next { background: #fff; color: #1B2A4A; }
    .btn-stop { background: #dc2626; color: #fff; }
    #runCtl { display: none; gap: 8px; margin-top: 10px; }
    #note { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #6b7280; padding: 8px 14px; background: #fff; border-bottom: 1px solid #e5e7eb; }
    #sheet { background: #fff; }
    #sheet .label { margin: 0 auto; border-bottom: 1px dashed #e5e7eb; }
    @media print {
      #bar, #note { display: none !important; }
      body { background: #fff; }
      #sheet .label { border-bottom: none; break-after: page; page-break-after: always; }
      #sheet .label:last-child { break-after: auto; page-break-after: auto; }
    }
  </style>
</head>
<body>
  <div id="bar">
    <h1>🖨 Batch print labels</h1>
    <div id="progress"></div>
    <div class="row">
      <span class="lbl">Per batch:</span>
      <button class="bs" type="button" data-n="10">10</button>
      <button class="bs" type="button" data-n="25">25</button>
      <button class="bs" type="button" data-n="50">50</button>
      <button id="startBtn" class="btn btn-go" type="button" style="margin-left:8px">Print all labels</button>
    </div>
    <div id="runCtl" class="row">
      <button id="nextBtn" class="btn btn-next" type="button" title="Force the next batch if the print dialog didn't advance">Next batch</button>
      <button id="stopBtn" class="btn btn-stop" type="button">Stop</button>
    </div>
  </div>
  <div id="note">Labels print ${LABEL_BATCH_DEFAULT} at a time so the printer never receives one oversized job. Each batch prints, then the next starts automatically. For hands-free runs, open in a <code>--kiosk-printing</code> browser.</div>
  <div id="sheet">${labelsHtml}</div>
</body>
</html>`);
      win.document.close();
      runLabelBatches(win, total);
    })
    .catch(() => onError?.('Could not generate labels'));
}

// Drives the batch pop-up from here. It used to be an inline script element written
// into the pop-up with onclick attributes on its buttons, which the app's CSP
// (inherited by a window.open('') pop-up) now refuses (pen test AD25996). The
// per-chunk `processed` guard mirrors the events batch-printer so a duplicate
// `afterprint` and a manual "Next batch" can't skip a batch.
function runLabelBatches(win, total) {
  const doc = win.document;
  const byId = (id) => doc.getElementById(id);
  const labels = [...doc.querySelectorAll('.label')];
  // activeChunk = index of the batch currently shown/printing; processed guards
  // each batch so it advances exactly once; advanceTimer debounces afterprint
  // so a burst of events collapses into a single advance.
  let batchSize = LABEL_BATCH_DEFAULT, start = 0, activeChunk = -1, running = false, processed = {}, advanceTimer = null;
  const chunks = () => Math.max(1, Math.ceil(total / batchSize));
  const render = () => {
    labels.forEach((el, i) => { el.style.display = (i >= start && i < start + batchSize) ? 'block' : 'none'; });
    const from = total === 0 ? 0 : start + 1, to = Math.min(start + batchSize, total);
    byId('progress').textContent = running
      ? `Batch ${Math.floor(start / batchSize) + 1} of ${chunks()} · labels ${from}–${to}`
      : `${total} label${total === 1 ? '' : 's'} selected · ${chunks()} batch${chunks() === 1 ? '' : 'es'} of ${batchSize}`;
    byId('startBtn').style.display = running ? 'none' : '';
    byId('runCtl').style.display = running ? 'flex' : 'none';
    doc.querySelectorAll('.bs').forEach(b => {
      b.disabled = running;
      b.className = 'bs' + (parseInt(b.getAttribute('data-n'), 10) === batchSize ? ' active' : '');
    });
  };
  const printCurrent = () => { activeChunk = Math.floor(start / batchSize); render(); win.focus(); win.setTimeout(() => win.print(), 60); };
  const stopRun = () => { running = false; if (advanceTimer) { win.clearTimeout(advanceTimer); advanceTimer = null; } render(); };
  const advance = () => {
    if (!running || activeChunk < 0 || processed[activeChunk]) return;
    processed[activeChunk] = true;
    start = (activeChunk + 1) * batchSize;
    if (start < total) printCurrent(); else stopRun();
  };
  doc.querySelectorAll('.bs').forEach(b => b.addEventListener('click', () => {
    if (running) return;
    batchSize = parseInt(b.getAttribute('data-n'), 10); start = 0; render();
  }));
  byId('startBtn').addEventListener('click', () => {
    if (running || total === 0) return;
    running = true; start = 0; processed = {}; render(); printCurrent();
  });
  byId('nextBtn').addEventListener('click', () => {
    if (!running) return;
    if (advanceTimer) { win.clearTimeout(advanceTimer); advanceTimer = null; }
    advance();
  });
  byId('stopBtn').addEventListener('click', stopRun);
  // Debounce + ~450ms settle: a burst of afterprint events collapses to one
  // advance, and the printer gets a breather before the next batch.
  win.addEventListener('afterprint', () => {
    if (!running) return;
    if (advanceTimer) win.clearTimeout(advanceTimer);
    advanceTimer = win.setTimeout(() => { advanceTimer = null; advance(); }, 450);
  });
  render();
}

// ─── ITEM HISTORY ────────────────────────────────────────────────────────────

function ItemHistory({ itemId }) {
  const [history, setHistory] = useState(null);
  useEffect(() => {
    api.get(`inventory/${itemId}/history`).then(setHistory).catch(() => setHistory([]));
  }, [itemId]);

  if (!history) return <div className="text-center py-3 text-[10px] text-gray-400">Loading history…</div>;
  if (history.length === 0) return <div className="text-center py-3 text-[10px] text-gray-400">No activity recorded</div>;

  return (
    <div className="mt-1 max-h-48 overflow-y-auto border border-gray-100 rounded">
      {history.map(h => (
        <div key={h.id} className="px-2 py-1.5 border-b border-gray-100 last:border-0">
          <div className="text-[9px] font-semibold">{h.reference}</div>
          <div className="text-[8px] text-gray-400">{h.person_name || 'System'} · {fmtDateTimeLong(h.created_at)}</div>
          {h.notes && <div className="text-[8px] text-gray-500 mt-0.5">{h.notes}</div>}
        </div>
      ))}
    </div>
  );
}

// ─── ADD INVENTORY MODAL ──────────────────────────────────────────────────────

function AddInventoryModal({ catalog, locations, prefill, onClose, onSave }) {
  const { toast } = useToast();
  const [catSearch, setCatSearch] = useState('');
  const [selectedCat, setSelectedCat] = useState(null);
  const [form, setForm] = useState({
    name: prefill?.name || '',
    category: prefill?.category || '',
    manufacturer: prefill?.manufacturer || '',
    serial_number: '',
    av_reference: '',
    location_id: '',
    purchase_price: prefill?.unit_cost != null ? String(prefill.unit_cost) : '',
    status: 'in_stock',
    quantity: '1',
    condition_state: 'new',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Auto-select catalog item if prefill has product_catalog_id
  useEffect(() => {
    if (prefill?.product_catalog_id && catalog.length > 0) {
      const cat = catalog.find(c => c.id === prefill.product_catalog_id);
      if (cat) {
        setSelectedCat(cat);
        setForm(p => ({ ...p, name: cat.description || p.name, manufacturer: cat.manufacturer || p.manufacturer, purchase_price: cat.unit_cost != null ? String(cat.unit_cost) : p.purchase_price }));
      }
    }
  }, [prefill, catalog]);

  const filteredCatalog = catSearch.length > 1
    ? catalog.filter(c => c.description?.toLowerCase().includes(catSearch.toLowerCase()) || c.manufacturer_part_number?.toLowerCase().includes(catSearch.toLowerCase()) || c.code?.toLowerCase().includes(catSearch.toLowerCase())).slice(0, 8)
    : [];

  const selectCat = (c) => {
    setSelectedCat(c);
    set('name', c.description || '');
    set('manufacturer', c.manufacturer || '');
    if (c.unit_cost != null) set('purchase_price', String(c.unit_cost));
    setCatSearch('');
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast('Name required', 'error'); return; }
    setSaving(true);
    try {
      await onSave({
        ...form,
        product_catalog_id: selectedCat?.id || prefill?.product_catalog_id || null,
        category: selectedCat?.category || form.category || 'Other',
        purchase_price: form.purchase_price !== '' ? parseFloat(form.purchase_price) : null,
        location_id: form.location_id || null,
        quantity: commitQuantity(form.quantity),
      });
    } catch (e) { toast(e.message || 'Failed', 'error'); setSaving(false); }
  };

  const qty = commitQuantity(form.quantity);

  return (
    <Modal title="Add Stock" onClose={onClose} wide
      footer={
        <><button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">{saving ? 'Adding…' : `Add ${qty > 1 ? qty + ' Items' : 'Item'}`}</button></>
      }>
      <div className="grid grid-cols-2 gap-2">
        {/* Catalog search */}
        <div className="col-span-2 relative">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Product Catalogue</label>
          {selectedCat ? (
            <div className="flex items-center gap-2 px-2 py-1.5 bg-gold/10 border border-gold/30 rounded text-[10px]">
              <span className="font-mono font-bold text-navy">{selectedCat.manufacturer_part_number || selectedCat.code}</span>
              <span className="text-gray-600 truncate">{selectedCat.description}</span>
              <button onClick={() => setSelectedCat(null)} className="ml-auto text-gray-400 hover:text-red-500">✕</button>
            </div>
          ) : (
            <div>
              <input className="form-input" placeholder="Search by product name or part no…" value={catSearch} onChange={e => setCatSearch(e.target.value)} />
              {filteredCatalog.length > 0 && (
                <div className="absolute z-10 left-0 right-0 top-full mt-0.5 bg-white border border-gray-200 rounded shadow-lg">
                  {filteredCatalog.map(c => (
                    <button key={c.id} type="button" onClick={() => selectCat(c)}
                      className="w-full text-left px-2 py-1.5 hover:bg-gold/10 text-[10px] flex gap-2">
                      <span className="font-mono font-bold text-navy whitespace-nowrap">{c.manufacturer_part_number || c.code}</span>
                      <span className="text-gray-600 truncate">{c.description}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Name *</label>
          <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Dell P2425H Monitor" />
        </div>
        {!selectedCat && (
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Category</label>
            <input className="form-input" value={form.category} onChange={e => set('category', e.target.value)} />
          </div>
        )}
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Manufacturer</label>
          <input className="form-input" value={form.manufacturer} onChange={e => set('manufacturer', e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Location</label>
          <select className="form-select" value={form.location_id} onChange={e => set('location_id', e.target.value)}>
            <option value="">No location</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Unit Price (£)</label>
          <input className="form-input" type="number" step="0.01" value={form.purchase_price} onChange={e => set('purchase_price', e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Initial Status</label>
          <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
            {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Quantity</label>
          <input className="form-input" type="number" min="1" max="200" value={form.quantity}
                 onChange={e => set('quantity', sanitiseQuantityInput(e.target.value))}
                 onBlur={() => set('quantity', String(commitQuantity(form.quantity)))} />
        </div>
        {qty === 1 && (
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Serial Number</label>
            <input className="form-input" value={form.serial_number} onChange={e => set('serial_number', e.target.value)} />
          </div>
        )}
        {qty > 1 && (
          <div className="col-span-2 text-[8px] text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            {qty} individual items will be created with sequential asset tags. Serial numbers can be entered individually after creation.
          </div>
        )}
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">AV Reference</label>
          <input className="form-input" value={form.av_reference} onChange={e => set('av_reference', e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Condition</label>
          <select className="form-select" value={form.condition_state} onChange={e => set('condition_state', e.target.value)}>
            {['new','good','fair','poor','damaged'].map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
          </select>
        </div>
      </div>
    </Modal>
  );
}

// ─── FAST SCAN MODE ──────────────────────────────────────────────────────────
// The lean "scan, scan, scan" screen. Entered by scanning the stock-take QR
// (?stocktake=ID), which sets activeStockTake so each subsequent item QR
// (?scan=TAG) lands back here and auto-confirms. Shows only the last few scans
// plus a running "to go" tally — never the full list.
const ST_RECENT_MAX = 6;
function readRecent(stId) { try { return JSON.parse(localStorage.getItem(`st_recent_${stId}`) || '[]'); } catch (e) { return []; } }
function writeRecent(stId, arr) { try { localStorage.setItem(`st_recent_${stId}`, JSON.stringify(arr.slice(0, ST_RECENT_MAX))); } catch (e) { /* ignore */ } }

export function StockTakeScanMode({ stId, scannedTag }) {
  const [summary, setSummary] = useState(null);   // { location_name, total, confirmed, status }
  const [recent, setRecent]   = useState(() => readRecent(stId));
  const [last, setLast]       = useState(null);    // result of the most recent confirm
  const [tag, setTag]         = useState('');
  const [busy, setBusy]       = useState(false);
  const handledTag = useRef(null);

  const pushRecent = (entry) => {
    setRecent(prev => { const next = [{ ...entry, ts: Date.now() }, ...prev].slice(0, ST_RECENT_MAX); writeRecent(stId, next); return next; });
  };

  const loadSummary = useCallback(() => {
    api.get(`stock-takes/${stId}/summary`).then(setSummary).catch(() => {});
  }, [stId]);

  const doConfirm = useCallback(async (raw) => {
    let t = String(raw || '').trim();
    const m = t.match(/[?&]scan=([^&]+)/i);   // accept a scanned full URL too
    if (m) t = decodeURIComponent(m[1]);
    if (!t) return;
    setBusy(true);
    try {
      const r = await api.post(`stock-takes/${stId}/confirm`, { asset_tag: t, method: 'scan' });
      setLast({ ...r, tag: r.item?.asset_tag || t });
      if (r.counts) setSummary(s => ({ ...(s || {}), total: r.counts.total, confirmed: r.counts.confirmed }));
      pushRecent({ tag: r.item?.asset_tag || t, name: r.item?.name || '', result: r.result, moved: r.moved });
    } catch (e) {
      const code = e?.data?.result;
      setLast({ result: code === 'not_found' ? 'not_found' : 'error', error: e?.data?.error || e.message, tag: t });
      pushRecent({ tag: t, name: '', result: code === 'not_found' ? 'not_found' : 'error' });
    }
    setBusy(false);
    setTag('');
  }, [stId]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  // Auto-confirm the tag this page was opened with (phone QR scan), once.
  useEffect(() => {
    if (scannedTag && handledTag.current !== scannedTag) { handledTag.current = scannedTag; doConfirm(scannedTag); }
  }, [scannedTag, doConfirm]);

  const stop = () => { try { localStorage.removeItem('activeStockTake'); } catch (e) {} window.location.href = `/?stocktake=${stId}&review=1`; };

  const total = summary?.total ?? 0;
  const confirmed = summary?.confirmed ?? 0;
  const remaining = Math.max(0, total - confirmed);
  const pct = total ? Math.round((confirmed / total) * 100) : 0;

  const RESULT = {
    confirmed: { t: '✓ Confirmed', c: 'bg-emerald-500' },
    added:     { t: '✓ Counted (added)', c: 'bg-emerald-500' },
    already:   { t: 'Already counted', c: 'bg-gray-400' },
    not_found: { t: '✗ Tag not recognised', c: 'bg-red-500' },
    error:     { t: '✗ Could not confirm', c: 'bg-red-500' },
  };

  return (
    <div className="min-h-screen bg-gray-50 p-3 max-w-md mx-auto select-none">
      <div className="flex items-center justify-between mb-2">
        <img src="/av-logo-colour.svg" alt="Alton Valley" className="h-5" />
        <span className="text-[9px] font-bold uppercase tracking-wide text-sky-600 bg-sky-50 border border-sky-200 rounded px-2 py-0.5">Stock take ON</span>
      </div>

      <div className="text-[13px] font-extrabold text-navy">{summary?.location_name || 'Stock take'}</div>

      {/* Big tally */}
      <div className="grid grid-cols-3 gap-2 my-3">
        <div className="bg-white border border-gray-200 rounded p-2 text-center"><div className="text-2xl font-extrabold text-emerald-600">{confirmed}</div><div className="text-[8px] uppercase font-bold text-gray-500">Counted</div></div>
        <div className="bg-white border border-gray-200 rounded p-2 text-center"><div className="text-2xl font-extrabold text-amber-600">{remaining}</div><div className="text-[8px] uppercase font-bold text-gray-500">To go</div></div>
        <div className="bg-white border border-gray-200 rounded p-2 text-center"><div className="text-2xl font-extrabold text-navy">{pct}%</div><div className="text-[8px] uppercase font-bold text-gray-500">Done</div></div>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-3"><div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} /></div>

      {/* Last scan result */}
      {last && (
        <div className={`rounded-lg p-3 mb-3 text-white ${(RESULT[last.result] || RESULT.error).c}`}>
          <div className="text-[13px] font-extrabold">{(RESULT[last.result] || RESULT.error).t}{last.moved ? ' · moved here' : ''}</div>
          <div className="text-[11px] opacity-90 font-mono">{last.tag}{last.item?.name ? ` — ${last.item.name}` : ''}</div>
          {last.error && <div className="text-[10px] opacity-90 mt-0.5">{last.error}</div>}
        </div>
      )}

      {/* Manual scan/type (works with USB/Bluetooth scanners too) */}
      <div className="flex gap-2 mb-3">
        <input autoFocus inputMode="text" className="form-input flex-1 text-[13px]" placeholder="Scan or type asset tag…"
          value={tag} onChange={e => setTag(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') doConfirm(tag); }} />
        <button onClick={() => doConfirm(tag)} disabled={busy || !tag.trim()} className="px-4 py-1 text-[12px] font-bold bg-navy text-white rounded disabled:opacity-40">Add</button>
      </div>
      <div className="text-[9px] text-gray-400 mb-3">Point the phone camera at each item’s QR label — it confirms automatically and returns here.</div>

      {/* Recent scans (only the last few) */}
      {recent.length > 0 && (
        <div className="bg-white border border-gray-200 rounded mb-3">
          <div className="px-3 py-1.5 text-[8px] font-bold uppercase text-gray-400 border-b">Last {recent.length} scanned</div>
          <div className="divide-y divide-gray-100">
            {recent.map((r, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${r.result === 'not_found' || r.result === 'error' ? 'bg-red-500' : r.result === 'already' ? 'bg-gray-300' : 'bg-emerald-500'}`} />
                <span className="text-[10px] font-mono font-semibold text-navy">{r.tag}</span>
                <span className="text-[9px] text-gray-500 truncate flex-1">{r.name}</span>
                {r.moved && <span className="text-[8px] text-sky-600 font-semibold">moved</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <button onClick={stop} className="w-full py-2.5 text-[12px] font-bold bg-red-600 text-white rounded hover:bg-red-700">
        ■ Stop stock take & review
      </button>
    </div>
  );
}

// ─── FULL-SCREEN STOCK TAKE PAGE (?stocktake=ID) ─────────────────────────────
// Default: enters fast scan mode. With ?review=1: leaves scan mode and shows the
// full session so the operator can act on outstanding / missing items.
export function StockTakeScanPage({ stockTakeId, review = false }) {
  const canManage = canAccess('inventory:manage');
  const [sows, setSows] = useState([]);

  useEffect(() => {
    if (review) {
      try { localStorage.removeItem('activeStockTake'); } catch (e) { /* ignore */ }
      api.get('sows').then(setSows).catch(() => setSows([]));
    } else {
      try { localStorage.setItem('activeStockTake', stockTakeId); } catch (e) { /* ignore */ }
    }
  }, [stockTakeId, review]);

  if (!review) return <StockTakeScanMode stId={stockTakeId} />;

  return (
    <div className="min-h-screen bg-gray-50 p-3 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-3">
        <img src="/av-logo-colour.svg" alt="Alton Valley" className="h-6" />
        <button onClick={() => { window.location.href = '/'; }} className="text-[10px] text-gray-500 border border-gray-200 rounded px-2 py-1">Done</button>
      </div>
      <StockTakeSession id={stockTakeId} sows={sows} canManage={canManage} />
    </div>
  );
}
